// src/views/CallConsole.jsx
//
// ═══════════════════════════════════════════════════════════════
// NEW CALL — the one screen a call runs on
// ═══════════════════════════════════════════════════════════════
// Replaces the old Marketing → Promote → Inquiry → Re-price → Calc crawl.
// One button, always on screen. Find the person by name / address / phone,
// see (and correct) everything already known about them, run the numbers,
// then land the call in exactly one of three places:
//
//   • Pitched            → the Pitch buttons inside Calc, straight to pipeline
//   • Fallout — Qualified → good deal, wrong moment. Keeps a follow-up date.
//   • Fallout — Denied    → doesn't work. Keeps the reason.
//
// Nothing gets thrown away either way: the numbers that were run, the notes
// that were taken, the phone number and mortgage detail that were corrected
// mid-call all get written back to the record with the timestamp they were
// gathered at. That is the whole point — the old archive button recorded a
// single boolean and lost the conversation.
//
// Both record types are handled through ONE normalized "prospect" shape
// (see prospectFromLoan / prospectFromLead in lib.js) so a mail-list row and
// a pipeline file behave identically here. A marketing lead only becomes a
// pipeline file at disposition time — pricing someone no longer litters the
// pipeline with files for calls that went nowhere.

import React, { useState, useMemo, useEffect, useCallback, useDeferredValue, useRef } from "react";
import { C, Ic } from '../theme';
import { Badge, Btn, CallBtn, Inp, MoneyInp } from '../ui';
import { CONTACT_LOG_MAX, FALLOUT_DENIED, FALLOUT_NOAPP, FALLOUT_QUAL, FALLOUT_REASONS, FALLOUT_STAGES, FALLOUT_TAGS, MANUAL_DEAL_LABEL, HS2_LOAN_TYPE, QUOTE_LOG_MAX, STC } from '../constants';
import {
  ageFromDobStr, blankProspect, buildProspectIndex, contactEntry, fmtWhen, isoDatePlus,
  hasRecordedLien, leadMetaFrom, leadMetaSet, markCallHandled, nextLoanId, prospectFromLead, prospectFromLoan,
  prospectToCalcLead, readCallLog, searchProspectIndex, collectDueFollowUps, searchLeadsRemote, searchLeadsFuzzy, outboundByPhone, fetchLeadById,
  ownerOf, leadOwner, withLeadOwner, stampLeadOwner, convertedLoanFor, leadFromDb,
} from '../lib';
import { STAFF, supabase } from '../supabase';
import {
  classifyAs, classifyQuery, kindLabel, nextKind, placeForNewLead, newLeadSummary, searchTermsFor,
  keepTextMatches, rankResults, combineResults, phoneDigits, formatPhoneInput, lettersTypedInPhone, extractPhone,
  splitPhoneTyping, partialPhoneForLead, cityKeySet,
  CLOSE_MATCH_BELOW, splitExactClose, findCloseMatches, fuzzyRemoteParams,
} from '../callSearch';
import { Calc } from './Calc';
import { CallbacksCard } from './Callbacks';
import { priorMcaMeaning, cltvText, cltvOf, HS2_ESTIMATE_TAG, payCents, LENDER_AMOUNT_OVERRIDE } from '../calcRules';
import { attachToLoan, fetchContacts, fetchScenarios, logContact, logScenario, rateEnvironment, scenarioRow } from '../scenarioStore';
import { useReportDealContext, dealContextFromProspect } from '../libraryChat';
import { useOwnershipUi } from '../features';
import { LeadFacts } from './LeadFacts';
import { leadFactsFor, factsCover, notesWithoutFacts, hasLitigatorTag, ON_FILE_TITLE } from '../convoGuide';

const num=function(v){var n=parseFloat(String(v==null?"":v).replace(/[^0-9.-]/g,""));return isNaN(n)?0:n;};
const $=function(n){return n||n===0?"$"+Math.round(n).toLocaleString():"—";};
// ── Zip → city/state for NEW leads ──
// public/zips.json is built from the lead table itself (zip5 → [City, ST]),
// so it covers the areas actually mailed. Fetched once, on first use, from
// our own origin (the CSP blocks third-party lookups). Unknown zips → null.
// Brand-new callers' deal-chip keys ("new@<opened ms>.<n>") — never shared.
let newCardSeq=0;
let zipTablePromise=null;
function zipTable(){
  if(typeof fetch!=="function")return Promise.resolve({});
  if(!zipTablePromise)zipTablePromise=Promise.resolve().then(function(){return fetch("/zips.json");})
    .then(function(r){return r&&r.ok?r.json():{};}).catch(function(){zipTablePromise=null;return {};});
  return zipTablePromise;
}
function lookupZip(zip){
  const z=String(zip||"").replace(/[^0-9]/g,"").slice(0,5);
  if(z.length!==5)return Promise.resolve(null);
  return zipTable().then(function(t){const hit=t&&t[z];return hit?{city:hit[0],state:hit[1]}:null;});
}
// The same table's city names tell Find where a name stops and a city starts
// ("John Smith Mesa AZ" — callSearch.classifyQuery's `cities` option). Built
// once; an empty/failed table is never cached, so it's asked for again later.
let zipCitiesPromise=null;
function zipCities(){
  if(!zipCitiesPromise)zipCitiesPromise=zipTable().then(function(t){
    const s=cityKeySet(Object.keys(t||{}).map(function(z){const v=t[z];return v&&v[0];}).filter(Boolean));
    if(!s.size)zipCitiesPromise=null;
    return s;
  });
  return zipCitiesPromise;
}

const falloutColor=function(stage){
  return stage===FALLOUT_QUAL?C.gold:stage===FALLOUT_DENIED?C.red:C.cyan;
};

// A loan keeps one free-text property string; a marketing lead keeps the parts
// separate because the mail merge needs them that way. Compose on read so the
// calculator and any new pipeline file always get a complete address.
// Apply a skip-trace number that just found this lead (see openPhoneMatch).
// Returns the same object when there's nothing to change.
// A 10-digit number typed into Find (no letters) — "" otherwise.
export function typedPhoneOf(q){
  const s=String(q||"");
  if(/[a-z]/i.test(s))return "";
  const d=s.replace(/\D/g,"");
  if(d.length===10)return d;
  if(d.length===11&&d.charAt(0)==="1")return d.slice(1);
  return "";
}
function fmtPhone10(d){return d.slice(0,3)+"-"+d.slice(3,6)+"-"+d.slice(6);}
// What a phone field held before a verified number replaced it, as a line for
// the notes — or "" when there's nothing worth keeping. Only a real phone
// (no letters, 10 digits or 1+10) is read as one; anything else that landed in
// the phone column (a street address off a bad import) is kept word for word
// instead of being mangled into a fake "Previous phone" from its last digits.
function previousPhoneLine(raw,notes){
  const s=String(raw||"").trim();
  if(!s)return "";
  const cur=typedPhoneOf(s);
  if(cur){return String(notes||"").replace(/\D/g,"").indexOf(cur)<0?"Previous phone: "+fmtPhone10(cur):"";}
  return String(notes||"").indexOf(s)<0?"Previous phone field: "+s:"";
}
// The number that reached them becomes the lead's phone; whatever was in the
// phone field before is kept in notes ("Previous phone" / "Previous phone
// field"). Logged either way.
export function applyVerifiedPhone(lead,phone){
  const d=String((phone&&phone.n)||"").replace(/\D/g,"").slice(-10);
  if(!lead||d.length!==10)return lead;
  const fmt=fmtPhone10(d);
  const cur=typedPhoneOf(lead.phone);
  if(cur===d)return lead;
  const notes=String(lead.notes||"");
  const how=(phone.type?phone.type+" ":"")+fmt+(phone.dnc?" (on the Public DNC list)":"");
  const keep=previousPhoneLine(lead.phone,notes);
  const entry=contactEntry("note","Phone "+how+" confirmed — set as the lead's phone."+(String(lead.phone||"").trim()?" What was in the phone field before is kept in notes.":""));
  const hist=(Array.isArray(lead.history)?lead.history:[]).concat([entry]);
  return Object.assign({},lead,{phone:fmt,history:hist,updated_at:new Date().toISOString()},
    keep?{notes:(notes?notes+"\n":"")+keep}:{});
}
// Same rule for a pipeline file (phone = ph, notes = leadNotes).
export function applyVerifiedPhoneToLoan(loan,d){
  if(!loan||String(d||"").length!==10)return loan;
  const cur=typedPhoneOf(loan.ph);
  if(cur===d)return loan;
  const notes=String(loan.leadNotes||"");
  const keep=previousPhoneLine(loan.ph,notes);
  return Object.assign({},loan,{ph:fmtPhone10(d)},keep?{leadNotes:(notes?notes+"\n":"")+keep}:{});
}
// ── Skip-trace DNC / TCPA litigator flags ──
// Those flags are for the Outbound dialer ONLY (Reece, 10/1): a flagged
// number never shows up there. Someone who calls IN on one is treated like
// any caller — the number is saved on their lead/file under the usual
// verified-phone rules and click-to-call works everywhere. A litigator's lead
// is still tagged (the tag keeps it out of the Outbound dialer) and
// the call is logged; the console shows an informational line, never a block.
export const LITIGATOR_TAG="tcpa-litigator";
export const CALLED_IN_FLAG_NOTE="This number is on the skip-trace DNC/litigator list — they called you, so calling back is fine";
export function isLitigatorLead(lead){return !!(lead&&(lead.tags||[]).indexOf(LITIGATOR_TAG)>=0);}
export function markLitigatorReached(lead,phone){
  if(!lead||isLitigatorLead(lead))return lead;
  const d=String((phone&&phone.n)||"").replace(/\D/g,"").slice(-10);
  const entry=contactEntry("note","Reached on a skip-trace number flagged TCPA litigator"+(d.length===10?" (ending "+d.slice(-4)+")":"")+" — they called in, so it's saved like any caller's number and calling back is fine. Kept off the Outbound dialer.");
  return Object.assign({},lead,{tags:(lead.tags||[]).concat([LITIGATOR_TAG]),
    history:(Array.isArray(lead.history)?lead.history:[]).concat([entry]),updated_at:new Date().toISOString()});
}
// The number that reached them goes on the lead like any caller's
// (applyVerifiedPhone); a litigator's number also tags the lead.
export function applyCallerPhone(lead,phone,litigator){
  const withPhone=applyVerifiedPhone(lead,phone);
  return litigator?markLitigatorReached(withPhone,phone):withPhone;
}
// Does any skip-trace row carrying this 10-digit number mark it a TCPA
// litigator's (the whole contact, or that one number)?
// A skip-trace match is a litigator's number when its own row is flagged, its
// own number is, or ANY row carrying that number is (m.crossLit, set when the
// matches were looked up).
export function phoneMatchIsLitigator(m){
  return !!(m&&((m.contact&&m.contact.litigator)||(m.phone&&m.phone.litigator)||m.crossLit));
}
export function contactsFlagLitigator(contacts,d){
  return (contacts||[]).some(function(c){
    if(!c)return false;
    if(c.litigator)return true;
    return (c.phones||[]).some(function(ph){return ph&&ph.litigator&&String(ph.n||"").replace(/\D/g,"").slice(-10)===d;});
  });
}
// Is this 10-digit number on the Do Not Call list on any skip-trace row?
export function contactsFlagDnc(contacts,d){
  return (contacts||[]).some(function(c){
    return !!c&&(c.phones||[]).some(function(ph){return ph&&ph.dnc&&String(ph.n||"").replace(/\D/g,"").slice(-10)===d;});
  });
}

// ── Find: what the Phone box found ──
// Under the two boxes once a full number is typed. Nothing found by phone →
// say so and send him to the Name/address box (the parent moves the focus).
// `flagged`: the number is on the skip-trace DNC / litigator list — said
// for information only; it is saved and dialable like any other.
function PhoneStatus({phone,searching,found,hasText,flagged,onStartNew}){
  if(searching)return <div style={{marginBottom:14,fontSize:12.5,color:C.textDim}}>Looking up {phone}…</div>;
  const info=flagged?<div className="cc-flag-note" style={{fontSize:12,color:C.textMuted,marginTop:4}}>ⓘ {CALLED_IN_FLAG_NOTE}.</div>:null;
  if(found)return(<div style={{marginBottom:14,fontSize:12.5,color:C.textMuted}}>
    Found by phone below.{hasText?"":" Not them? Type their name or address above."}{info}
  </div>);
  return(<div style={{marginBottom:16,padding:"12px 16px",borderRadius:12,border:"1px solid "+C.border,background:C.surfaceAlt,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
    <div style={{flex:"1 1 240px",minWidth:0}}>
      <div style={{fontSize:13,fontWeight:800,color:C.text}}>No lead with this number — type their name or address above.</div>
      <div style={{fontSize:12,color:C.textMuted,marginTop:2}}>{phone} will be saved on whoever you open or create.</div>
      {info}
    </div>
    {!hasText&&<Btn small onClick={onStartNew}>{"+ New lead with "+phone}</Btn>}
  </div>);
}

// Database search with wildcard patterns from callSearch (house number +
// street name, either name order) — the never-mailed leads aren't in memory.
// Read-only; any failure is just "nothing extra found".
function searchLeadPatterns(patterns,limit){
  if(!supabase||!patterns||!patterns.length)return Promise.resolve([]);
  const or=patterns.map(function(p){return p.col+".ilike."+p.pat;}).join(",");
  try{
    return Promise.resolve(supabase.from("marketing_leads").select("*").or(or).limit(limit||25))
      .then(function(r){return r&&!r.error?(r.data||[]).map(leadFromDb):[];},function(){return [];});
  }catch(e){return Promise.resolve([]);}
}

// The action bar's ends under the corner bubbles: 56px bubble + its 20px
// gutter + 16px air (phones: 12px gutter + 56 + 12).
const CALL_BAR_DOCK_PX=92;
const CALL_BAR_CSS="@media (max-width:560px){.cc-actions.cc-dock-l{padding-left:80px!important}.cc-actions.cc-dock-r{padding-right:80px!important}}";

// Find-step layout: two stacked boxes, full width; 16px+ type so iOS never
// zooms, and a 16px gutter on a phone.
const FIND_CSS=
  ".cc-find{max-width:1080px;margin:0 auto;padding:28px 24px 60px}"+
  ".cc-find-input{font-size:18px;padding:16px 44px 16px 50px}"+
  ".cc-find-input.cc-phone{font-size:20px;letter-spacing:.3px}"+
  // Result rows: the name shrinks before the badges do on a desktop; on a
  // phone the row wraps and the badges get their own line under the name,
  // lined up with it (34px avatar + 14px gap), so the name is never crushed.
  ".cc-row-main{flex:1 1 0;min-width:0}"+
  ".cc-row-badges{display:flex;align-items:center;gap:8px;flex-shrink:0}"+
  "@media (max-width:639px){.cc-find{padding:16px 16px 48px}.cc-find-input{font-size:17px;padding:14px 40px 14px 44px}.cc-find-input.cc-phone{font-size:18px}.cc-find .cc-open-hint{display:none}"+
  ".cc-row{flex-wrap:wrap;row-gap:8px}.cc-row-main{flex:1 1 calc(100% - 48px)}"+
  ".cc-row-badges{flex:1 1 100%;flex-wrap:wrap;padding-left:48px;box-sizing:border-box;min-width:0}}";
const FIND_LABEL={display:"block",fontSize:10,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.7,marginBottom:6};
const FIND_ICON={position:"absolute",left:17,top:"50%",transform:"translateY(-50%)",color:C.textDim,pointerEvents:"none",display:"inline-flex"};
const FIND_BOX={width:"100%",borderRadius:14,background:C.surface,color:C.text,outline:"none",fontFamily:"'Mulish',system-ui,sans-serif",boxSizing:"border-box",boxShadow:"0 2px 14px rgba(11,42,74,0.07)"};
const FIND_CLEAR={position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:C.textDim,cursor:"pointer",fontSize:22,lineHeight:1,padding:"4px 6px"};

// Reference line for a reverse-refi lead's recorded lien (never a balance).
// HECMs record at 150% of the max claim, so lien ÷ 1.5 is the max claim at
// origination — the appraised value at that point in time, or, when it
// matches that year's FHA limit, a home worth at least the limit
// (priorMcaMeaning; before 2009 limits were per county, so it hedges). The
// origination year is the 4-digit run anywhere in the field, so "2023-02-16",
// "02/16/2023" and "03/2019" all read right.
export function lienNote(p,hecmLimit){
  if(!p||!(p.lienAmount>0))return null;
  const fmt=n=>"$"+Math.round(n).toLocaleString();
  if(!p.lienIsHecm)return "Recorded lien "+fmt(p.lienAmount)+" (reference only — not what they owe)";
  const mca=p.lienAmount/1.5;
  const pm=priorMcaMeaning(mca,p.origDate,num(hecmLimit)||null);
  return "Recorded HECM lien "+fmt(p.lienAmount)+" ÷ 1.5 = "+fmt(mca)+" max claim at origination"+(pm.anomaly?", ":" = ")+pm.text+" · not what they owe";
}

// Which of the card's three deal types is picked: "hecm" (refi of an
// existing reverse) | "ftr" (first-time) | "hs2" (HomeSafe Second — the
// forward first stays). An HS2 pick is never a reverse (lib.isReverseFrom).
function dealKindOf(p){
  if(!p)return"ftr";
  if(p.dealType==="hs2")return"hs2";
  return p.isReverse?"hecm":"ftr";
}

function fullAddr(p){
  if(!p)return"";
  if(p.kind==="loan")return p.address||"";
  var line=[p.street||"",[p.city||"",p.state||""].filter(Boolean).join(", ")].filter(Boolean).join(", ");
  return (line+(p.zip?" "+p.zip:"")).trim()||p.address||"";
}

// Loan-shaped patch built from the editable prospect draft. Everything the
// loans table has a real column for goes in flat; the rest rides in leadMeta
// (packed into follow_ups._wf by loanToDb).
function loanPatchFrom(p){
  return{
    b:p.name||"",ph:p.phone||"",em:p.email||"",prop:fullAddr(p),
    dob:p.dob||"",age:ageFromDobStr(p.dob)||p.age||0,
    hv:num(p.homeValue),mb:num(p.mortgageBalance),
    type:p.loanType||"HECM",condo:!!p.condo,
    leadNotes:p.notes||"",
    followUpDate:p.followUpDate||"",followUpNote:p.followUpNote||"",
    leadMeta:Object.assign({curRate:p.curRate||"",lender:p.lender||"",origDate:p.origDate||"",
      curPL:p.curPL||"",curAvail:p.curAvail||"",estCC:p.estCC||"",dealType:p.dealType||"",goal:p.goal||"",isReverse:!!p.isReverse,dob:p.dob||"",
      // The existing reverse's Adjustable / Fixed as last picked in the calculator.
      existingLoan:p.existingLoan||""},
      // The mail-list lien reference rides along so a file minted from a lien
      // lead still shows "Recorded lien …" and never reads as free and clear.
      num(p.lienAmount)>0?{lienAmount:num(p.lienAmount),lienIsHecm:!!p.lienIsHecm}:{}),
  };
}
// Mail-list columns + profile meta from the draft — the lead-side mirror of
// loanPatchFrom. On a lien lead the typed balance lives in meta.curBalance and
// loan_balance keeps the recorded lien.
function leadPatchFrom(p,lien){
  const cols={name:p.name||"",phone:p.phone||"",email:p.email||"",
    address:p.street||p.address||"",city:p.city||"",state:p.state||"",zip:p.zip||"",
    home_value:num(p.homeValue),loan_type:p.loanType||"",rate:p.curRate||"",original_lender:p.lender||"",
    origination_date:p.origDate||"",notes:p.notes||"",follow_up_date:p.followUpDate||null};
  if(!lien)cols.loan_balance=num(p.mortgageBalance);
  const meta={dob:p.dob||"",curPL:p.curPL||"",curAvail:p.curAvail||"",estCC:p.estCC||"",dealType:p.dealType||"",goal:p.goal||"",
    isReverse:!!p.isReverse,condo:!!p.condo,followUpNote:p.followUpNote||"",existingLoan:p.existingLoan||""};
  if(lien)meta.curBalance=num(p.mortgageBalance);
  return{cols:cols,meta:meta};
}
// A loan's leadMeta can carry the lien reference of the lead it came from.
function withLienMeta(pr){
  if(!pr||pr.kind!=="loan"||!pr.raw||num(pr.lienAmount)>0)return pr;
  const m=pr.raw.leadMeta||{};
  return num(m.lienAmount)>0?Object.assign({},pr,{lienAmount:num(m.lienAmount),lienIsHecm:!!m.lienIsHecm}):pr;
}

// ── Write only what was edited ──
// The draft `p` is a snapshot taken when the card opened. Writing the whole
// snapshot back on every note/letter/save reverted anything that changed the
// record meanwhile — Calc's pitch (value, type, the cleared fallout
// follow-up), the borrower portal, the other staff member. Every commit now
// writes only the fields that differ from the snapshot the card was opened
// from, i.e. what was actually typed on this call.
const EDIT_KEYS=["name","phone","email","address","street","city","state","zip","dob","homeValue","mortgageBalance","loanType","condo",
  "curRate","lender","origDate","curPL","curAvail","estCC","dealType","goal","isReverse","notes","followUpDate","followUpNote","existingLoan"];
const sameVal=function(a,b){
  if(a===b)return true;
  const ea=a==null||a==="",eb=b==null||b==="";
  if(ea||eb)return ea&&eb;
  if(typeof a==="boolean"||typeof b==="boolean")return !!a===!!b;
  return String(a)===String(b);
};
// The record the draft was opened from, as a prospect — "unchanged" means equal to this.
function baseProspect(draft){
  if(!draft||!draft.raw)return null;
  if(draft.kind==="loan")return withLienMeta(prospectFromLoan(draft.raw));
  if(draft.kind==="lead")return prospectFromLead(draft.raw);
  return null;
}
// Keys of `cur` whose value differs from `base` (leadMeta compared key by
// key). No base (a brand-new caller) → everything counts.
function changedOnly(cur,base){
  if(!base)return cur;
  const out={};
  Object.keys(cur).forEach(function(k){
    if(k==="leadMeta"){
      const bm=base.leadMeta||{};const m={};let any=false;
      Object.keys(cur.leadMeta||{}).forEach(function(j){if(!sameVal(cur.leadMeta[j],bm[j])){m[j]=cur.leadMeta[j];any=true;}});
      if(any)out.leadMeta=m;
      return;
    }
    if(!sameVal(cur[k],base[k]))out[k]=cur[k];
  });
  return out;
}
function dirtyLoanPatch(draft){
  const b=baseProspect(draft);
  return changedOnly(loanPatchFrom(draft),b?loanPatchFrom(b):null);
}
function dirtyLeadPatch(draft,lien){
  const b=baseProspect(draft);
  const cur=leadPatchFrom(draft,lien),base=b?leadPatchFrom(b,lien):null;
  return{cols:changedOnly(cur.cols,base&&base.cols),meta:changedOnly(cur.meta,base&&base.meta)};
}
// Which record, which edited fields, which pricing runs — equal signatures
// mean a second write would say nothing new.
function draftSig(draft,runs){
  if(!draft)return "";
  const d=draft.kind==="loan"?dirtyLoanPatch(draft):draft.kind==="lead"?dirtyLeadPatch(draft,hasRecordedLien(draft.raw)):null;
  return draft.kind+":"+draft.id+"|"+JSON.stringify(d)+"|"+(runs||[]).map(function(q){return q&&q.epoch;}).join(",");
}
// Anything typed on this card that isn't on the record yet?
function hasEdits(draft){
  if(!draft)return false;
  if(draft.kind==="loan")return Object.keys(dirtyLoanPatch(draft)).length>0;
  if(draft.kind==="lead"){const d=dirtyLeadPatch(draft,hasRecordedLien(draft.raw));return Object.keys(d.cols).length+Object.keys(d.meta).length>0;}
  return false;
}
// The draft with every editable field EXCEPT `keys` reset to the snapshot, so
// a commit of it writes only those keys (the notes autosave).
function limitTo(draft,keys){
  const b=baseProspect(draft);
  if(!b)return draft;
  const out=Object.assign({},draft);
  EDIT_KEYS.forEach(function(k){if(keys.indexOf(k)<0)out[k]=b[k];});
  return out;
}
const MONEY_KEYS=["homeValue","mortgageBalance","curPL","curAvail","estCC"];
// Prospect for a pipeline file, opened in place of the lead it was converted
// from. Whatever was edited on the lead's card comes along, and so does the
// lead's lien reference and litigator flag.
function fileProspect(file,leadDraft){
  const next=withLienMeta(prospectFromLoan(file));
  next.viaLeadId=(leadDraft&&leadDraft.id)||null;
  next.alreadyFile=true;                       // drives the "Already a file" banner
  if(!leadDraft)return next;
  const b=baseProspect(leadDraft);
  const moved=function(k){return !b||!(MONEY_KEYS.indexOf(k)>=0?num(leadDraft[k])===num(b[k]):sameVal(leadDraft[k],b[k]));};
  EDIT_KEYS.forEach(function(k){if(k!=="street"&&k!=="city"&&k!=="state"&&k!=="zip"&&k!=="address"&&b&&moved(k))next[k]=leadDraft[k];});
  if(b&&["street","city","state","zip","address"].some(moved)&&fullAddr(leadDraft))next.address=fullAddr(leadDraft);
  if(!(num(next.lienAmount)>0)&&num(leadDraft.lienAmount)>0){next.lienAmount=num(leadDraft.lienAmount);next.lienIsHecm=!!leadDraft.lienIsHecm;}
  if(leadDraft.litigator||isLitigatorLead(leadDraft.raw))next.litigator=true;
  if(leadDraft.doNotContact)next.doNotContact=true;
  return next;
}
// Merge this call's pricing runs into a record's capped quote log. Deduped by
// epoch and kept in time order, so committing the same runs again (every
// note, letter and save commits) never stacks copies that push real history
// out of the cap — and no run priced during the call is dropped.
function mergeQuotes(log,runs){
  const seen=Object.create(null);const out=[];
  (log||[]).concat(runs||[]).forEach(function(q){
    if(!q)return;
    const k=q.epoch!=null?"e"+q.epoch:(q.at?"a"+q.at:null);
    if(k){if(seen[k])return;seen[k]=1;}
    out.push(q);
  });
  const t=function(q){return q.epoch||Date.parse(q.at||"")||0;};
  out.sort(function(a,b){return t(a)-t(b);});
  return out.slice(-QUOTE_LOG_MAX);
}
// Two pricing runs that say exactly the same thing (same inputs, same
// results, same market) — a re-click of Run, or Calc's auto re-run landing on
// what it already showed. The repeat adds nothing but would use a slot of the
// capped log, so it isn't recorded twice.
function sameRun(a,b){
  if(!a||!b)return false;
  const sig=function(q){return JSON.stringify([q.inputs||{},q.top||[],q.productCount||0,q.rateEnv||null]);};
  return sig(a)===sig(b);
}
// Calc fills its DOB box from a bare age as "<year>-06-15". That placeholder
// isn't a birthday anyone gave, so it never goes back onto the record.
function isAgePlaceholderDob(dob,age){
  return !!age&&String(dob||"")===(new Date().getFullYear()-age)+"-06-15";
}
// Read a draft ref and empty it in one step — whoever takes it first writes
// it, so the same note can never be saved twice.
function takeText(ref){
  const t=String(ref.current||"").trim();
  ref.current="";
  return t;
}
const LETTER_CAMPAIGN="Handwritten Letter";   // the one campaign the Letters tracker counts
const staffName=function(k){return (STAFF&&STAFF[k]&&STAFF[k].name)||(k==="max"?"Max":"Reece");};
const staffShort=function(k){return (STAFF&&STAFF[k]&&STAFF[k].short)||(k==="max"?"M":"R");};
// A contact entry rendered the way the existing loan activity log expects, so
// the call history also shows up in Loan Detail's timeline.
function toActivity(e){
  return{text:e.text,type:e.type==="disposition"?"stage":"note",timestamp:e.date+" "+e.time,date:e.date,time:e.time,epoch:e.epoch};
}

// ── Small presentational bits ─────────────────────────────────
const Field=function(props){
  return(<div style={{flex:props.wide?"1 1 100%":"1 1 "+(props.basis||"46%"),minWidth:props.min||150}}>
    <label style={{display:"block",fontSize:9.5,fontWeight:800,color:C.textDim,marginBottom:5,textTransform:"uppercase",letterSpacing:.6}}>{props.label}</label>
    {props.children}
  </div>);
};
const SectionTitle=function(props){
  return(<div style={{display:"flex",alignItems:"center",gap:8,margin:props.first?"0 0 12px":"22px 0 12px"}}>
    <span style={{fontSize:12,fontWeight:800,color:C.accentLight,textTransform:"uppercase",letterSpacing:.7}}>{props.children}</span>
    <div style={{flex:1,height:1,background:C.rule}}/>
    {props.right}
  </div>);
};
// The little "Saved" that follows a field that saves itself.
const SavedHint=function({show}){
  return show?<span style={{fontSize:10.5,fontWeight:700,color:C.green}}>✓ Saved</span>:null;
};
// ── Owner chip ──
// Owner is a tracking tag ("whoever works it owns it"), not privacy. The chip
// shows it; the menu is the only way to hand a lead or file to the other
// person — working a record never overwrites an owner it already has.
// `owner` null = nobody yet; `dflt` is who it counts as meanwhile (files with
// no owner count as Reece's everywhere else in the app).
function OwnerChip({owner,dflt,onSet,note}){
  const [open,setOpen]=useState(false);
  const shown=owner||dflt||null;
  // Mounted only while the owner UI is on (`ownerChip` below, src/features.js).
  return(<span data-owner-chip="" style={{position:"relative",display:"inline-flex"}} onMouseLeave={function(){setOpen(false);}}>
    <button onClick={function(){if(onSet)setOpen(function(o){return !o;});}}
      title={(owner?"Owned by "+staffName(owner):dflt?"No owner set — counts as "+staffName(dflt)+"'s until someone works it":"No owner yet — whoever works it owns it")+(onSet?". Click to reassign.":"")}
      style={{display:"inline-flex",alignItems:"center",gap:6,padding:"3px 10px 3px 4px",borderRadius:20,cursor:onSet?"pointer":"default",fontSize:11,fontWeight:700,fontFamily:"'Mulish',system-ui,sans-serif",
        border:"1px solid "+(owner?C.navy+"40":C.border),background:owner?C.navy+"10":"transparent",color:owner?C.navy:C.textDim}}>
      <span style={{width:18,height:18,borderRadius:"50%",display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:9.5,fontWeight:800,
        background:shown?C.navy:C.border,color:shown?C.cream:C.textDim}}>{shown?staffShort(shown):"?"}</span>
      {shown?"Owner: "+staffName(shown):"No owner yet"}{note?<span style={{fontWeight:600,opacity:.7}}>{note}</span>:null}{onSet?" ▾":""}
    </button>
    {open&&<div style={{position:"absolute",top:"100%",left:0,zIndex:30,paddingTop:4}}>
      <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:10,boxShadow:"0 8px 24px rgba(11,42,74,0.16)",padding:4,minWidth:150}}>
        <div style={{fontSize:9.5,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.6,padding:"5px 9px 4px"}}>Reassign to</div>
        {["reece","max"].map(function(k){
          const on=k===owner;
          return <button key={k} onClick={function(){setOpen(false);if(!on)onSet(k);}} style={{display:"flex",alignItems:"center",gap:8,width:"100%",padding:"7px 9px",borderRadius:7,border:"none",cursor:on?"default":"pointer",textAlign:"left",fontSize:12.5,fontWeight:on?800:600,fontFamily:"'Mulish',system-ui,sans-serif",background:on?C.navy+"10":"transparent",color:C.text}}>
            <span style={{width:18,height:18,borderRadius:"50%",display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:9.5,fontWeight:800,background:C.navy,color:C.cream}}>{staffShort(k)}</span>
            {staffName(k)}{on?" ✓":""}
          </button>;
        })}
      </div>
    </div>}
  </span>);
}

// ═══════════════════════════════════════════════════════════════
// `onOpenFile(loanId)` (App's glue — Reece, 10/10/26: "When you click on
// pitch, it automatically takes you to that deal"): a pitch from the embedded
// calculator folds the call into the file (handlePitched), closes the console
// — the pitch IS the call's outcome, no disposition — and opens that file's
// Loan Detail. Without it (an older host) the "Pitched … Done" banner stays.
export function CallConsole({open,onClose,staff,loans,setLoans,mktgLeads,setMktgLeads,leadsReady,leadLoadProgress,allLeadsLoaded,adoptLeads,hecmLimit,rateSheets,setRateSheets,setNav,onOpenFile,seed,onRegisterFlush,dockLeft,dockRight,settings,rcAuthed,practice}){
  // Who is working the call. Stamped as owner on anything unowned he works,
  // and on every new lead/file minted here ("whoever works it owns it").
  const staffKey=staff==="max"?"max":"reece";
  // "Lead assignment & Max on files" (src/features.js, off by default): off,
  // the card has no owner chip / reassign menu; the stamping above goes on.
  const ownershipUi=useOwnershipUi();
  const [step,setStep]=useState("find");        // find | card | calc
  // ── Find: two boxes ──
  // Phone up top (formatted as typed); name OR address below it. What the
  // second box holds is read by callSearch.classifyQuery — the chip under it
  // shows the reading and a click flips it (kept while the text is unchanged).
  const [phoneQ,setPhoneQ]=useState("");
  const [textQ,setTextQ]=useState("");
  const [kindPick,setKindPick]=useState(null);  // {text, kind} — a chip flip
  const [p,setP]=useState(null);                // the editable prospect draft
  // The deal-basics chip in the Ask-the-Library bubble follows the card on
  // screen (state/age/value/type only — never name, phone or address). Its
  // key names THIS card and is minted once, when the card opens
  // (openProspect): saving a new caller as a lead, Run or a pitch re-basing
  // the card onto a file never changes it, so a ✕ stays with that caller for
  // the whole call — and every brand-new caller gets a key of their own
  // (an unsaved caller has no id to share).
  const cardKey=useRef("");
  useReportDealContext(open&&p&&step!=="find"?dealContextFromProspect(p):null,"call",p?cardKey.current:"");
  const [noteDraft,setNoteDraftState]=useState("");
  // ── Notes save themselves ──
  // The note box, the disposition note and the two free-text fields on the
  // card ("What they're after", the follow-up "About") never need a Save
  // click. Refs mirror the live values so a blur, a close, switching to
  // someone else or an unmount can flush exactly once — the take*() helpers
  // empty the ref as they read it, which is what keeps an entry from ever
  // being written twice.
  const pRef=useRef(null);pRef.current=p;
  const noteDraftRef=useRef("");
  const setNoteDraft=function(v){noteDraftRef.current=v;setNoteDraftState(v);};
  const dispoNoteRef=useRef("");
  const noteFieldsDirty=useRef(false);          // goal / followUpNote typed but not yet written
  const noteFieldTimer=useRef(null);
  const [savedKey,setSavedKey]=useState("");    // "goal" | "followUpNote" — the little "Saved" hint
  // Every pricing run of THIS call, oldest first. lastQuote below is just the
  // newest one, for display; this is what gets written (see mergeQuotes).
  const sessionQuotes=useRef([]);
  // Set by handlePitched: once the pitch has landed in `loans`, the draft is
  // re-based onto the pitched file (see the effect further down).
  const rebaseAfterPitch=useRef(null);
  // What the last write said (record + edited fields + runs), so a flush with
  // nothing new to add doesn't touch the record again.
  const lastCommitSig=useRef("");
  const openedAt=useRef(0);                     // when this card was opened (ms)
  const [lastQuote,setLastQuote]=useState(null);
  const [ranNumbers,setRanNumbers]=useState(false);
  // Letters confirmed while this card is open (the record snapshot in p.raw
  // doesn't see them until the card is reopened).
  const [sessionLetters,setSessionLetters]=useState([]);
  const [pitched,setPitched]=useState(null);    // {product, loanId}
  const [dispo,setDispo]=useState(null);        // {stage}
  const [dispoReason,setDispoReason]=useState("");
  const [dispoNote,setDispoNoteState]=useState("");
  const setDispoNote=function(v){dispoNoteRef.current=v;setDispoNoteState(v);};
  const [dispoFollowUp,setDispoFollowUp]=useState("");
  const [dispoDnc,setDispoDnc]=useState(false);
  // Did this session actually WORK the lead (typed something, logged a note)?
  // Only a worked call gets asked how it ended — opening a record to read it
  // and closing again should never demand a disposition.
  const [touched,setTouched]=useState(false);
  const [flash,setFlash]=useState("");
  const [callLog,setCallLog]=useState([]);
  const [callEpoch,setCallEpoch]=useState(null);// the inbound call this console is answering
  const [editDetails,setEditDetails]=useState(false); // "On file" block: read-only until asked
  const phoneRef=useRef(null);
  const textRef=useRef(null);

  // ── Phone-first flow ──
  // The Phone box is looked up by phone (pipeline, leads, the skip-trace
  // list). If nobody has it, focus drops to the Name/address box. Whatever
  // lead is then opened gets the typed number applied (it's the number
  // they're on) — or, on a brand-new lead, it's their phone.
  const typedPhone=typedPhoneOf(phoneQ);
  const deferredPhone=useDeferredValue(phoneQ);
  const deferredText=useDeferredValue(textQ);
  // The cities in our own zip table, once loaded: "John Smith Mesa AZ" then
  // reads as a name + Mesa, AZ (not a city called "John Smith Mesa").
  const [knownCities,setKnownCities]=useState(null);
  useEffect(function(){
    if(!open||knownCities)return;
    let live=true;
    zipCities().then(function(s){if(live&&s&&s.size)setKnownCities(s);},function(){});
    return function(){live=false;};
  },[open]);// eslint-disable-line react-hooks/exhaustive-deps
  const clsOpts=useMemo(function(){return knownCities?{cities:knownCities}:null;},[knownCities]);
  const pickFor=function(text){return kindPick&&kindPick.text===text?kindPick.kind:null;};
  const autoCls=useMemo(function(){return classifyQuery(textQ,clsOpts);},[textQ,clsOpts]);
  const textCls=useMemo(function(){return classifyAs(textQ,pickFor(textQ),clsOpts);},[textQ,kindPick,clsOpts]);// eslint-disable-line react-hooks/exhaustive-deps
  const searchCls=useMemo(function(){return classifyAs(deferredText,pickFor(deferredText),clsOpts);},[deferredText,kindPick,clsOpts]);// eslint-disable-line react-hooks/exhaustive-deps
  const terms=useMemo(function(){return searchTermsFor(searchCls);},[searchCls]);
  // Phone digits worth searching on: 4+ ("read me back the last four").
  const phoneSearch=(function(){const d=phoneDigits(deferredPhone);return d.length>=4?d.slice(-10):"";})();
  const [phoneChecked,setPhoneChecked]=useState("");

  // ── Search indexes ──
  // Only built while the console is open. The lead index is ~70K rows; paying
  // for it on every background render of the app would be absurd.
  const loanIdx=useMemo(function(){return open?buildProspectIndex(loans,"loan"):[];},[open,loans]);
  const leadIdx=useMemo(function(){return open?buildProspectIndex(mktgLeads,"lead"):[];},[open,mktgLeads]);
  // ── Server-side fallback ──
  // Only mailed/worked leads are in memory; the never-mailed ones live in the
  // database. The Phone box (7+ digits) and the Name/address box (3+
  // characters) also ask the server (debounced), so a caller from the
  // unmailed list is still found. An address or a two-word name also gets a
  // wildcard search ("4521*maple*", either name order) so "N" vs "North" or
  // "Smith John" vs "John Smith" can't hide anyone. Results are indexed the
  // same way as local ones and merged without duplicates.
  // Two independent lookups, each re-run only when ITS box changes — typing
  // a name under a number never re-sends the phone query (each is a scan of
  // the ~115K-row lead table), and editing the number never re-sends the name.
  // A third, close-match lookup (remoteFz, search_leads_fuzzy) only runs once
  // the exact ones came back thin — see "Close matches" below.
  const NO_REMOTE={key:"",rows:[],loading:false};
  const [remotePh,setRemotePh]=useState(NO_REMOTE);
  const [remoteTx,setRemoteTx]=useState(NO_REMOTE);
  const [remoteFz,setRemoteFz]=useState(NO_REMOTE);
  const remote={phoneKey:remotePh.key,phoneRows:remotePh.rows,textKey:remoteTx.key,textRows:remoteTx.rows,
    fuzzyKey:remoteFz.key,fuzzyRows:remoteFz.rows,loading:remotePh.loading||remoteTx.loading||remoteFz.loading};
  const remotePhone=phoneSearch.length>=7?phoneSearch:"";
  const remoteText=terms.remote.length>=3?terms.remote:"";
  const remotePatKey=terms.patterns.map(function(x){return x.col+"."+x.pat;}).join(",");
  const remoteTextKey=remoteText||remotePatKey?remoteText+"|"+remotePatKey:"";
  // Nothing to ask (or everything is in memory now, or the console closed):
  // clear it, and never leave a "Searching the full database…" spinning.
  const clearRemote=function(r){return r.loading||r.rows.length||r.key?NO_REMOTE:r;};
  useEffect(function(){
    if(!open||allLeadsLoaded!==false||!remotePhone){setRemotePh(clearRemote);return;}
    let live=true;
    setRemotePh(function(r){return Object.assign({},r,{loading:true});});
    const t=setTimeout(function(){
      searchLeadsRemote(remotePhone,25).then(function(res){return (res&&res.data)||[];},function(){return [];})
        .then(function(rows){if(live)setRemotePh({key:remotePhone,rows:rows,loading:false});});
    },350);
    return function(){live=false;clearTimeout(t);};
  },[open,allLeadsLoaded,remotePhone]);// eslint-disable-line react-hooks/exhaustive-deps
  useEffect(function(){
    if(!open||allLeadsLoaded!==false||!remoteTextKey){setRemoteTx(clearRemote);return;}
    let live=true;
    setRemoteTx(function(r){return Object.assign({},r,{loading:true});});
    const text=remoteText,pats=terms.patterns;
    const t=setTimeout(function(){
      Promise.all([
        text?searchLeadsRemote(text,25).then(function(res){return (res&&res.data)||[];},function(){return [];}):Promise.resolve([]),
        pats.length?searchLeadPatterns(pats,25):Promise.resolve([]),
      ]).then(function(res){
        if(!live)return;
        const seen=new Set();
        const rows=res[0].concat(res[1]).filter(function(m){if(!m||seen.has(m.id))return false;seen.add(m.id);return true;});
        setRemoteTx({key:remoteTextKey,rows:rows,loading:false});
      });
    },350);
    return function(){live=false;clearTimeout(t);};
  },[open,allLeadsLoaded,remoteTextKey]);// eslint-disable-line react-hooks/exhaustive-deps
  // ── Skip-trace phone match ──
  // A full 10-digit number in the Phone box is checked against the outbound
  // (skip-traced) list. A hit suggests the lead that number belongs to.
  // Nothing is written until he opens it (see openPhoneMatch).
  const [phoneMatches,setPhoneMatches]=useState([]);
  // The typed number when it sits on a TCPA litigator's skip-trace row (the
  // contact is flagged, or that one number is) — "" otherwise. Checked across
  // every row that carries it, including rows with no lead attached. Only
  // used to tag the lead and say so — the number is saved and dialable.
  const [phoneLitFor,setPhoneLitFor]=useState("");
  // The typed number when a skip-trace row has it on the Do Not Call list.
  const [phoneDncFor,setPhoneDncFor]=useState("");
  const phoneCheckFailed=useRef("");
  useEffect(function(){
    if(!open)return;
    const d=typedPhoneOf(deferredPhone);
    if(d.length!==10){setPhoneMatches([]);setPhoneChecked("");setPhoneLitFor("");setPhoneDncFor("");return;}
    let live=true;
    outboundByPhone(d).then(async function(contacts){
      const out=[];
      for(const c of contacts){
        if(!c.lead_id)continue;
        let lead=(mktgLeads||[]).find(function(m){return m.id===c.lead_id;});
        if(!lead)lead=await fetchLeadById(c.lead_id);
        if(lead)out.push({contact:c,lead:lead,phone:(c.phones||[]).find(function(p){return p.n===d;})||null});
      }
      // The number is a litigator's if ANY row carrying it says so — every
      // match for it carries that, not just the litigator's own row.
      const lit=contactsFlagLitigator(contacts,d);
      const dnc=contactsFlagDnc(contacts,d);
      out.forEach(function(m){m.crossLit=lit;});
      if(live){phoneCheckFailed.current="";setPhoneMatches(out);setPhoneChecked(d);setPhoneLitFor(lit?d:"");setPhoneDncFor(dnc?d:"");}
    }).catch(function(){
      // Lookup failed: stop saying "Looking up…", but don't treat the number
      // as checked — typedNumberIsLitigator asks again when a record opens.
      if(live){phoneCheckFailed.current=d;setPhoneMatches([]);setPhoneChecked(d);setPhoneLitFor("");setPhoneDncFor("");}
    });
    return function(){live=false;};
  },[open,deferredPhone]);// eslint-disable-line react-hooks/exhaustive-deps
  // A number that found the lead is proof it's theirs, so opening the match
  // makes it the lead's phone (applyVerifiedPhone); whatever was in the phone
  // field before is kept in notes. Logged on the timeline either way.
  // A skip-trace DNC / litigator number is no exception — they called in, so
  // it's saved like any caller's and click-to-call works. A litigator's lead is
  // also tagged (keeps it out of the Outbound dialer).
  const typedIsLitigator=!!typedPhone&&phoneLitFor===typedPhone;
  const typedIsFlagged=typedIsLitigator||(!!typedPhone&&phoneDncFor===typedPhone);
  // When a record opens on a typed number: is it a litigator's (to tag the
  // lead)? Uses the finished lookup when there is one, otherwise asks (opening
  // a result can beat the background check). A failed lookup is just "no".
  const typedNumberIsLitigator=function(){
    if(!typedPhone)return Promise.resolve(false);
    if(phoneChecked===typedPhone&&phoneCheckFailed.current!==typedPhone)return Promise.resolve(phoneLitFor===typedPhone);
    return Promise.resolve().then(function(){return outboundByPhone(typedPhone);}).then(function(cs){return contactsFlagLitigator(cs,typedPhone);},function(){return false;});
  };
  // Tag the lead a pipeline file came from (the file is what gets the number).
  const tagLitigatorLead=function(leadId,phone){
    if(!leadId)return;
    setMktgLeads(function(prev){return prev.map(function(l){return l&&l.id===leadId?markLitigatorReached(l,phone):l;});});
  };
  const openPhoneMatch=async function(m){
    if(adoptLeads&&!(mktgLeads||[]).some(function(x){return x.id===m.lead.id;}))adoptLeads([m.lead]);
    // Litigator if this row says so OR the number sits on any litigator's row
    // (two skip-trace rows can share a number). When the cross-row check isn't
    // in hand it is asked now; if it can't be answered the lead just isn't
    // tagged — the number is saved either way.
    let litig=phoneMatchIsLitigator(m);
    const d=String((m.phone&&m.phone.n)||"").replace(/\D/g,"").slice(-10);
    if(!litig&&d.length===10&&m.crossLit===undefined){
      try{const cs=await outboundByPhone(d);litig=contactsFlagLitigator(cs,d);}catch(e){litig=false;}
    }
    // Already a pipeline file: the verified number goes on the FILE (that's
    // what gets worked from here on), not the mail-list row.
    const file=convertedLoanFor(m.lead,loans);
    if(file){
      const lp=applyVerifiedPhoneToLoan(file,d);
      if(lp!==file)setLoans(function(prev){return prev.map(function(l){return l.id===lp.id?lp:l;});});
      if(litig)tagLitigatorLead(m.lead.id,m.phone);
      openProspect(Object.assign(fileProspect(lp,prospectFromLead(m.lead)),litig?{litigator:true}:{}));
      return;
    }
    const updated=applyCallerPhone(m.lead,m.phone,litig);
    if(updated!==m.lead)setMktgLeads(function(prev){return prev.map(function(l){return l.id===updated.id?updated:l;});});
    openProspect(Object.assign(prospectFromLead(updated),litig?{litigator:true}:{}));
  };
  // ── Results: phone matches first, then name/address matches ──
  // Phone hits are rows whose phone holds the typed digits (never a
  // house-number or zip that happens to contain them). Text hits search what
  // classifyQuery read: an address on house number + street name (ranked by
  // city/zip), a name on its words in any order. A row found both ways is
  // the strongest match and goes on top. Each row carries `via`.
  const remotePhoneIdx=useMemo(function(){return remote.phoneRows.length?buildProspectIndex(remote.phoneRows,"lead"):[];},[remote.phoneRows]);
  const remoteTextIdx=useMemo(function(){return remote.textRows.length?buildProspectIndex(remote.textRows,"lead"):[];},[remote.textRows]);
  const remoteFuzzyIdx=useMemo(function(){return remote.fuzzyRows.length?buildProspectIndex(remote.fuzzyRows,"lead"):[];},[remote.fuzzyRows]);
  // Exact text hits, best first. A hit whose street type or direction isn't
  // what was typed ("4521 Maple Rd" → 4521 MAPLE AVE) moves to the close list.
  const textFound=useMemo(function(){
    if(!open||!terms.locals.length)return{exact:[],close:[]};
    let hits=[];
    terms.locals.forEach(function(q){
      hits=hits.concat(keepTextMatches(searchProspectIndex([loanIdx,leadIdx],q,40),q));
    });
    if(remote.textKey===remoteTextKey&&remoteTextIdx.length){
      // A database row only has to look like what was typed: the wildcard
      // patterns already matched it on the server.
      hits=hits.concat(remoteTextIdx);
    }
    const seen=new Set();
    hits=hits.filter(function(r){const k=r.kind+"-"+r.id;if(seen.has(k))return false;seen.add(k);return true;});
    return splitExactClose(rankResults(hits,searchCls),searchCls);
  },[open,loanIdx,leadIdx,remote.textKey,remoteTextKey,remoteTextIdx,terms,searchCls]);
  // ── Close matches ──
  // Fewer than CLOSE_MATCH_BELOW exact hits → also list near-spellings
  // (callSearch.findCloseMatches: same house number + a street name a typo
  // away, a name a typo away in any order), labelled "Close match" and always
  // under the exact ones. With leads still in the database, the database is
  // asked too (search_leads_fuzzy) — but only once its exact search has also
  // come back thin, and its rows are only candidates: each one is checked
  // here with the same rules. Until that SQL is run the function is missing
  // and lib.searchLeadsFuzzy quietly returns nothing.
  const exactCount=textFound.exact.length;
  const fuzzyParams=useMemo(function(){return fuzzyRemoteParams(searchCls);},[searchCls]);
  const fuzzyKey=fuzzyParams?[fuzzyParams.kind,fuzzyParams.q,fuzzyParams.house,fuzzyParams.zip].join("|"):"";
  const remoteTextDone=!remoteTextKey||(remoteTx.key===remoteTextKey&&!remoteTx.loading);
  const askFuzzy=!!fuzzyKey&&remoteTextDone&&exactCount<CLOSE_MATCH_BELOW;
  const fuzzyFetched=useRef("");
  useEffect(function(){
    if(!open||allLeadsLoaded!==false||!fuzzyKey){fuzzyFetched.current="";setRemoteFz(clearRemote);return;}
    if(!askFuzzy||fuzzyFetched.current===fuzzyKey){
      setRemoteFz(function(r){return r.loading?Object.assign({},r,{loading:false}):r;});
      return;
    }
    let live=true;
    const params=fuzzyParams,k=fuzzyKey;
    setRemoteFz(function(r){return Object.assign({},r,{loading:true});});
    const t=setTimeout(function(){
      // Up to 100 candidates: the database's trigram test is loose on short
      // words (it has to be, to catch "Jon Smyth"), and every row is checked
      // again here before it is shown.
      searchLeadsFuzzy(params,100).then(function(res){return (res&&res.data)||[];},function(){return [];})
        .then(function(rows){if(!live)return;fuzzyFetched.current=k;setRemoteFz({key:k,rows:rows,loading:false});});
    },150);
    return function(){live=false;clearTimeout(t);};
  },[open,allLeadsLoaded,fuzzyKey,askFuzzy]);// eslint-disable-line react-hooks/exhaustive-deps
  const closeHits=useMemo(function(){
    if(!open||!terms.locals.length)return[];
    const idx=exactCount>=CLOSE_MATCH_BELOW?[]
      :[loanIdx,leadIdx].concat(remote.fuzzyKey===fuzzyKey&&remoteFuzzyIdx.length?[remoteFuzzyIdx]:[]);
    return findCloseMatches(idx,searchCls,{exclude:textFound.exact,also:textFound.close,limit:20});
  },[open,terms,exactCount,loanIdx,leadIdx,remote.fuzzyKey,fuzzyKey,remoteFuzzyIdx,searchCls,textFound]);
  const results=useMemo(function(){
    if(!open)return[];
    let phoneHits=[];
    if(phoneSearch){
      const byDigits=function(r){return !!r.digits&&r.digits.indexOf(phoneSearch)>=0;};
      phoneHits=searchProspectIndex([loanIdx,leadIdx],phoneSearch,40).filter(byDigits)
        .concat(remote.phoneKey===remotePhone?remotePhoneIdx.filter(byDigits):[]);
    }
    return combineResults(phoneHits,textFound.exact,50,closeHits);
  },[open,loanIdx,leadIdx,phoneSearch,remotePhone,remotePhoneIdx,remote.phoneKey,textFound,closeHits]);
  const exactShown=results.filter(function(r){return !r.close;}).length;
  const closeShown=results.length-exactShown;
  // Rows only the Phone box found — listed last, under the close matches, and
  // counted on their own so "1 match" never means the phone's owner.
  const phoneOnlyShown=results.filter(function(r){return r.via==="phone";}).length;
  const phoneSearching=!!typedPhone&&(phoneChecked!==typedPhone||(allLeadsLoaded===false&&remote.phoneKey!==typedPhone));
  const foundByPhone=phoneMatches.length>0||results.some(function(r){return r.via!=="text";});
  // Nothing by phone → straight to the Name/address box (once per number
  // typed: clearing the box, or the console opening again — the same caller
  // ringing back — arms it again).
  const focusedForPhone=useRef("");
  useEffect(function(){if(!typedPhone)focusedForPhone.current="";},[typedPhone]);
  useEffect(function(){
    if(!open||step!=="find"||!typedPhone||phoneSearching||foundByPhone)return;
    if(focusedForPhone.current===typedPhone)return;
    focusedForPhone.current=typedPhone;
    if(textRef.current&&!String(textQ||"").trim())textRef.current.focus();
  },[open,step,typedPhone,phoneSearching,foundByPhone]);// eslint-disable-line react-hooks/exhaustive-deps
  // The opening focus on the Phone box waits a beat (the overlay mounts
  // first) — it must never steal the focus back from Name/address when a
  // fast lookup has already sent it there.
  const focusPhoneSoon=function(){
    setTimeout(function(){
      if(phoneRef.current&&!(textRef.current&&document.activeElement===textRef.current))phoneRef.current.focus();
    },60);
  };

  // ── Full history from the database ──
  // The jsonb copy on the record is capped; these tables aren't. Loaded when a
  // prospect is opened so the timeline shows EVERY scenario ever priced for
  // this person, not just the last twelve. Falls back silently to the jsonb
  // copy when the migration hasn't been run.
  const [history,setHistory]=useState({loaded:false,scenarios:[],contacts:[]});
  const historyKey=p?(p.kind+":"+p.id):"";
  useEffect(function(){
    if(!open||!p||!p.id){setHistory({loaded:false,scenarios:[],contacts:[]});return;}
    let cancelled=false;
    const loanId=p.kind==="loan"?p.id:null;
    const leadId=p.kind==="lead"?p.id:null;
    // A promoted lead's history lives under the lead id too — ask for both.
    const alsoLead=p.kind==="loan"?((p.raw&&p.raw.sourceLeadId)||p.viaLeadId||null):null;
    Promise.all([
      fetchScenarios(loanId,leadId||alsoLead,100),
      fetchContacts(loanId,leadId||alsoLead,200),
    ]).then(function(r){
      if(cancelled)return;
      setHistory({loaded:true,scenarios:r[0]||[],contacts:r[1]||[]});
    });
    return function(){cancelled=true;};
  },[open,historyKey]);// eslint-disable-line react-hooks/exhaustive-deps

  // ── Open / reset ──
  // Initialize ONLY on the closed→open edge. Re-running whenever `seed`
  // changes identity would wipe a call that is already in progress.
  const prevOpen=useRef(false);
  useEffect(function(){
    if(open&&!prevOpen.current){
      setCallLog(readCallLog());
      focusedForPhone.current="";
      if(seed&&seed.prospect){
        openProspect(seed.prospect,seed.callEpoch||null);
      }else if(seed&&seed.phone&&/[a-z]/i.test(String(seed.phone))){
        // A name, not a number (the Re-price Radar seeds a borrower's name
        // when the scenario has no phone): search it in Name/address. A whole
        // number inside it still goes up to the Phone box.
        const ex=extractPhone(seed.phone);
        const who=ex?ex.rest:String(seed.phone).trim();
        setStep("find");setPhoneQ(ex?formatPhoneInput(ex.phone):"");setTextQ(who);setKindPick(null);setP(null);setCallEpoch(seed.callEpoch||null);
        setTimeout(function(){const el=textRef.current;if(el){el.focus();try{el.setSelectionRange(who.length,who.length);}catch(e){}}},60);
      }else if(seed&&seed.phone){
        // Opened from an inbound-call toast: the caller's number goes in the
        // Phone box and is looked up straight away (no match → the focus drops
        // to Name/address for whatever he hears next).
        setStep("find");setPhoneQ(formatPhoneInput(phoneDigits(seed.phone)));setTextQ("");setKindPick(null);setP(null);setCallEpoch(seed.callEpoch||null);
        focusPhoneSoon();
      }else{
        setStep("find");setPhoneQ("");setTextQ("");setKindPick(null);setP(null);setCallEpoch(null);
        focusPhoneSoon();
      }
    }
    prevOpen.current=open;
  },[open,seed]);// eslint-disable-line react-hooks/exhaustive-deps

  // Esc closes (through the same guard as the X button, so a priced call still
  // gets asked how it ended) — except inside a control that handles its own
  // Esc (`data-own-esc`: the Calls to return Done chooser closes itself).
  useEffect(function(){
    if(!open)return;
    const h=function(e){if(e.key==="Escape"&&!dispo){if(e.target&&e.target.closest&&e.target.closest("[data-own-esc]"))return;e.stopPropagation();requestClose();}};
    window.addEventListener("keydown",h,true);
    return function(){window.removeEventListener("keydown",h,true);};
  });// no dep array on purpose — requestClose closes over live state

  const flashMsg=function(m){setFlash(m);setTimeout(function(){setFlash("");},2600);};

  // ── After a pitch, the card IS the pitched file ──
  // The draft was a snapshot of the lead (or of the file before Calc revived
  // and re-priced it). Once the pitch has landed in `loans`, re-base onto the
  // live file so a later note, save or second pitch goes to the file and
  // writes only what is typed from here on — never the old snapshot's value,
  // type or fallout follow-up back over what Calc just wrote.
  useEffect(function(){
    const r=rebaseAfterPitch.current;
    if(!r)return;
    const file=(loans||[]).find(function(l){return l.id===r.loanId;});
    if(!file)return;
    rebaseAfterPitch.current=null;
    const next=withLienMeta(prospectFromLoan(file));
    if(r.viaLeadId)next.viaLeadId=r.viaLeadId;
    if(r.litigator)next.litigator=true;
    if(r.doNotContact)next.doNotContact=true;
    if(!(num(next.lienAmount)>0)&&r.lienAmount>0){next.lienAmount=r.lienAmount;next.lienIsHecm=r.lienIsHecm;}
    pRef.current=next;
    // handlePitched already merged this call's runs into the file.
    lastCommitSig.current=draftSig(next,sessionQuotes.current);
    setP(next);
  },[loans]);

  // ══════════════════════════════════════════════════════════════
  // PERSISTENCE
  // ══════════════════════════════════════════════════════════════

  // A brand-new caller becomes a marketing lead the moment we need an id for
  // them (entering the calculator, logging a note, dispositioning). Returns
  // the prospect with kind/id filled in — callers must use the RETURN value,
  // not the state variable, because setState hasn't landed yet.
  // viaLetter: created just to log a handwritten letter — they never called,
  // so it lands as a mailed prospect, not a responded call-in.
  const ensurePersisted=useCallback(function(draft,viaLetter){
    if(draft.kind!=="new")return draft;
    const id="MK-"+Date.now().toString(36).toUpperCase();
    const now=new Date().toISOString();
    const src=viaLetter&&(!draft.source||draft.source==="Call-In")?"Manual":(draft.source||"Call-In");
    const row={
      id:id,name:draft.name||(viaLetter?"(no name)":"(unnamed caller)"),phone:draft.phone||"",email:draft.email||"",
      address:draft.street||draft.address||"",city:draft.city||"",state:draft.state||"",zip:draft.zip||"",
      home_value:num(draft.homeValue),loan_balance:num(draft.mortgageBalance),
      loan_type:draft.loanType||"",rate:draft.curRate||"",original_lender:draft.lender||"",
      origination_date:draft.origDate||"",list_source:src,source:src,
      status:viaLetter?"New":"Responded",responded:!viaLetter,responded_at:viaLetter?null:now,response_method:viaLetter?"":"Call",
      notes:draft.notes||"",
      // Whoever adds the lead owns it (or whoever was picked on the card); a
      // caller on a TCPA-litigator number is tagged from the start (keeps the
      // lead off the Outbound dialer) — the number they
      // called from is still saved and dialable like anyone's.
      tags:[viaLetter?"handwritten":"call-in","owner:"+(draft.owner==="max"||draft.owner==="reece"?draft.owner:staffKey)].concat(draft.litigator?[LITIGATOR_TAG]:[]),mailings:[],
      history:leadMetaSet([contactEntry("created",viaLetter?"Added to send a handwritten letter":"Added from a live call")].concat(draft.litigator?
        [contactEntry("note","Called from a number flagged TCPA litigator on the skip-trace list — they called in, so it's saved like any caller's number and calling back is fine. Kept off the Outbound dialer.")]:[]),{
        dob:draft.dob||"",curPL:draft.curPL||"",curAvail:draft.curAvail||"",estCC:draft.estCC||"",dealType:draft.dealType||"",goal:draft.goal||"",
        isReverse:!!draft.isReverse,condo:!!draft.condo,followUpNote:draft.followUpNote||"",existingLoan:draft.existingLoan||"",lastContactAt:now,quoteLog:[]}),
      follow_up_date:draft.followUpDate||null,
      doNotContact:false,do_not_contact:false,
      created_at:now,updated_at:now,
    };
    setMktgLeads(function(prev){return prev.concat([row]);});
    const next=Object.assign({},draft,{kind:"lead",id:id,statusLabel:row.status,raw:row});
    // The ref moves now, not on the next render: a second write in the same
    // click (save, then the close-out flush) must find the lead just minted
    // instead of minting another.
    pRef.current=next;
    setP(next);
    return next;
  },[setMktgLeads,staffKey]);

  // Write the draft (plus any entries logged during this call) back to
  // whichever table owns the record. Only fields edited on this card are
  // written (see dirtyLoanPatch / dirtyLeadPatch) — never the whole snapshot.
  // Every pricing run of the call is merged into the capped quote log, and an
  // unowned record is stamped with whoever is working it.
  const commit=useCallback(function(draft,entries,extraLoan,extraLead){
    const ent=entries||[];
    const now=new Date().toISOString();
    const runs=sessionQuotes.current.slice();
    if(draft.kind==="loan"||draft.kind==="lead")lastCommitSig.current=draftSig(draft,runs);
    if(draft.kind==="loan"){
      const dirty=dirtyLoanPatch(draft);
      const metaPatch=dirty.leadMeta;delete dirty.leadMeta;
      setLoans(function(prev){return prev.map(function(l){
        if(l.id!==draft.id)return l;
        return Object.assign({},l,dirty,metaPatch?{leadMeta:Object.assign({},l.leadMeta||{},metaPatch)}:{},{
          contactLog:(l.contactLog||[]).concat(ent).slice(-CONTACT_LOG_MAX),
          quoteLog:mergeQuotes(l.quoteLog,runs),
          activityLog:(l.activityLog||[]).concat(ent.map(toActivity)),
          lastContactAt:now,
          owner:l.owner||staffKey,
        },extraLoan||{});
      });});
      return draft;
    }
    if(draft.kind==="lead"){
      setMktgLeads(function(prev){return prev.map(function(m){
        if(m.id!==draft.id)return m;
        const meta=leadMetaFrom(m.history);
        const d=dirtyLeadPatch(draft,hasRecordedLien(m));
        const cols=Object.assign({},d.cols);
        // A cleared box never blanks these — they came off the list.
        ["name","loan_type","original_lender","origination_date"].forEach(function(k){if(k in cols&&!cols[k])delete cols[k];});
        const nextMeta=Object.assign({},meta,d.meta,{
          lastContactAt:now,
          quoteLog:mergeQuotes(meta.quoteLog,runs),
        });
        const baseHist=(Array.isArray(m.history)?m.history:[]).filter(function(h){return !(h&&h.type==="profile");});
        const row=Object.assign({},m,cols,{
          history:leadMetaSet(baseHist.concat(ent).slice(-CONTACT_LOG_MAX),nextMeta),
          updated_at:now,
        },(typeof extraLead==="function"?extraLead(m):extraLead)||{});
        return stampLeadOwner(row,staffKey);
      });});
      return draft;
    }
    return draft;
  },[setLoans,setMktgLeads,staffKey]);

  // Mint a pipeline file for a lead that has been dispositioned. Carries the
  // entire captured record across — this is the file the Fallout board and
  // Loan Detail read from.
  const createLoanFromProspect=useCallback(function(draft,stage,entries,extra){
    const ts=new Date();
    const id=nextLoanId(loans,staffKey);
    // Owner: one picked on a new caller's card, else the lead's own owner,
    // else whoever is on the call. (A brand-new file — nothing overwritten.)
    const pick=draft.owner==="max"||draft.owner==="reece"?draft.owner:null;
    const fileOwner=pick||leadOwner(liveLeadOf(draft.raw||{id:draft.id}))||staffKey;
    const loan=Object.assign({
      id:id,
      stage:stage,fha:null,
      src:draft.source||"Marketing Lead",ref:null,dmCampaign:draft.dmCampaign||"",
      comm:0,estClose:"TBD",
      deal:{},docs:{hoa:null,payoff:null,ins:null,app:null},followUps:{},
      uploads:[],pushedDocs:[],portalLogins:[],statementData:null,tasks:[],
      scenarioHistory:[],
      contactLog:(draft.contactLog||[]).concat(entries||[]).slice(-CONTACT_LOG_MAX),
      quoteLog:mergeQuotes(draft.quoteLog,sessionQuotes.current.slice()),
      activityLog:(entries||[]).map(toActivity).concat([{
        text:"File created from lead "+draft.id+" — "+stage,type:"stage",
        timestamp:ts.toLocaleString(),date:ts.toLocaleDateString(),
        time:ts.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),epoch:ts.getTime(),
      }]),
      lastContactAt:ts.toISOString(),
      archived:false,queue:null,sourceLeadId:draft.id,
      owner:fileOwner,
    },loanPatchFrom(draft),{stage:stage},
    // A HomeSafe Second call that falls out is filed as one (a lead's own
    // loan_type is the list's record of the mortgage they already have).
    dealKindOf(draft)==="hs2"?{type:HS2_LOAN_TYPE}:{},extra||{});
    setLoans(function(prev){return prev.concat([loan]);});
    return loan;
  },[loans,setLoans,staffKey,mktgLeads]);// eslint-disable-line react-hooks/exhaustive-deps

  // ══════════════════════════════════════════════════════════════
  // ACTIONS
  // ══════════════════════════════════════════════════════════════

  // Mirror one contact entry into the uncapped contact_log table. The jsonb
  // copy on the record is still written (it is the offline cache and the
  // pre-migration fallback); this is the copy that survives the cap.
  function writeContact(prospect,entry,extra){
    if(!prospect||!prospect.id||!entry)return;
    logContact(Object.assign({
      loan_id:prospect.kind==="loan"?prospect.id:null,
      lead_id:prospect.kind==="lead"?prospect.id:null,
      at:entry.at||new Date().toISOString(),
      kind:entry.type||"note",
      body:entry.text||"",
      phone:prospect.phone||"",
      borrower_name:prospect.name||"",
    },extra||{}));
  }

  // ── A lead that already became a file is worked AS that file ──
  // Opening it again (a search hit, the skip-trace list, Outbound, a call-back)
  // must not pitch or fall out a second copy. The live lead row is checked,
  // not the snapshot, so a conversion made since it was loaded still counts.
  function liveLeadOf(lead){
    if(!lead||!lead.id)return lead;
    return (mktgLeads||[]).find(function(m){return m.id===lead.id;})||lead;
  }
  function fileFor(draft){
    if(!draft||draft.kind!=="lead")return null;
    return convertedLoanFor(liveLeadOf(draft.raw||{id:draft.id}),loans);
  }
  // Switch a lead draft to the file it became (carrying anything typed on the
  // lead's card across). Returns the draft to write through.
  function toFileIfConverted(draft){
    const file=fileFor(draft);
    if(!file)return draft;
    const next=fileProspect(file,draft);
    pRef.current=next;
    setP(next);
    return next;
  }

  function openProspect(prospect,epoch){
    // Whoever was open before keeps every note and edit typed on them.
    flushPending();
    let next=prospect;
    if(next&&next.kind==="lead"){const file=fileFor(next);if(file)next=fileProspect(file,next);}
    if(next&&next.kind==="loan")next=withLienMeta(next);
    pRef.current=next;
    openedAt.current=Date.now();
    cardKey.current=next&&next.id?(next.kind||"lead")+":"+next.id:"new@"+openedAt.current+"."+(++newCardSeq);
    setP(next);
    setStep("card");
    setNoteDraft("");
    setDispoNote("");
    setLastQuote(null);
    sessionQuotes.current=[];
    rebaseAfterPitch.current=null;
    lastCommitSig.current="";
    noteFieldsDirty.current=false;
    setSavedKey("");
    setRanNumbers(false);
    setPitched(null);
    setTouched(false);
    setEditDetails(false);
    setSessionLetters([]);
    if(epoch!==undefined&&epoch!==null)setCallEpoch(epoch);
  }
  async function openResult(r){
    if(r.kind==="lead"&&r.raw&&adoptLeads&&!(mktgLeads||[]).some(function(m){return m.id===r.id;}))adoptLeads([r.raw]);
    // A close match is a guess ("Ron Smith" for the "Don Smith" he typed), so
    // the number he's on only goes on it once he says so. One the phone also
    // found already has that number.
    const guess=!!(r.close&&r.via!=="both");
    const takePhone=!!(typedPhone&&r.raw)&&(!guess||window.confirm("Save "+fmtPhone10(typedPhone)+" as "+(r.name||"this person")+"'s phone?\n\n"+
      "This is a close match — not exactly what you typed. OK saves the number you're on to their record; Cancel just opens it."));
    if(takePhone){
      // The number he's on reaches them, so it becomes their phone — a skip-
      // trace DNC / litigator number too (they called in; calling back is
      // fine). A litigator's lead is tagged as well.
      const lit=await typedNumberIsLitigator();
      if(r.kind==="lead"){
        const file=convertedLoanFor(liveLeadOf(r.raw),loans);
        if(file){
          // Already a pipeline file: the number goes on the FILE.
          const lp=applyVerifiedPhoneToLoan(file,typedPhone);
          if(lp!==file)setLoans(function(prev){return prev.map(function(l){return l.id===lp.id?lp:l;});});
          if(lit)tagLitigatorLead(r.raw.id,{n:typedPhone});
          openProspect(Object.assign(fileProspect(lp,prospectFromLead(r.raw)),lit?{litigator:true}:{}));return;
        }
        const updated=applyCallerPhone(r.raw,{n:typedPhone},lit);
        if(updated!==r.raw)setMktgLeads(function(prev){return prev.map(function(l){return l.id===updated.id?updated:l;});});
        openProspect(Object.assign(prospectFromLead(updated),lit?{litigator:true}:{}));return;
      }
      const lp=applyVerifiedPhoneToLoan(r.raw,typedPhone);
      if(lp!==r.raw)setLoans(function(prev){return prev.map(function(l){return l.id===lp.id?lp:l;});});
      if(lit&&lp.sourceLeadId)tagLitigatorLead(lp.sourceLeadId,{n:typedPhone});
      openProspect(Object.assign(prospectFromLoan(lp),lit?{litigator:true}:{}));return;
    }
    openProspect(r.kind==="loan"?prospectFromLoan(r.raw):prospectFromLead(r.raw));
  }
  // A brand-new lead from what's in the two boxes, each part in its place:
  // the Phone box → phone, a name → Name, an address → Street / City / State
  // / Zip (callSearch.placeForNewLead), name + address → both. Nothing typed
  // ever lands in the wrong field — an address typed into Find used to
  // become the lead's name.
  async function startNew(){
    const cls=classifyAs(textQ,pickFor(textQ),clsOpts);
    const place=placeForNewLead(cls);
    // The number is saved on the new lead like any caller's — a skip-trace
    // litigator's too (the lead is tagged when it's saved). Asked now if the
    // background check hasn't answered yet.
    const lit=typedPhone?await typedNumberIsLitigator():false;
    // Not a full number: a local 7–9 digits is kept so it's on the card to
    // finish; a non-US number as typed; a "last four" lookup is not a phone.
    const phone=typedPhone?fmtPhone10(typedPhone):partialPhoneForLead(phoneQ);
    openProspect(Object.assign(blankProspect(phone),place,lit?{litigator:true}:{}));
    // A zip without its city/state: fill them from our own zip table (the same
    // lookup the card's Zip box does), never over anything parsed.
    if(place.zip&&(!place.city||!place.state)){
      const z5=String(place.zip).slice(0,5);
      lookupZip(place.zip).then(function(hit){
        if(!hit)return;
        setP(function(prev){
          if(!prev||prev.kind!=="new"||String(prev.zip||"").slice(0,5)!==z5)return prev;
          return Object.assign({},prev,{city:prev.city||hit.city,state:prev.state||hit.state});
        });
      });
    }
  }
  // ── The two Find boxes ──
  // Letters typed into the Phone box are a name or address typed there by
  // habit — they move to the Name/address box, and the typing carries on
  // there. Nothing typed is ever dropped. A whole phone number typed or
  // pasted into the Name/address box moves up to the Phone box.
  function focusTextAtEnd(v){
    setTimeout(function(){const el=textRef.current;if(el){el.focus();try{el.setSelectionRange(v.length,v.length);}catch(e){}}},0);
  }
  function onPhoneChange(v){
    const moved=lettersTypedInPhone(v);
    if(moved!==null){
      const cur=String(textQ||"");
      const join=function(t){return !cur.trim()?t:(/\s$/.test(cur)?cur:cur+" ")+t;};
      // A paste or dictation (more than one character at once), or letters
      // arriving in an empty Phone box, is a whole thing said at once — not
      // keystrokes: "4521 N Maple St, Mesa, AZ 85201" pasted here kept its
      // house number up in Phone and the new lead lost it, and "(480)
      // 555-1234 4521 N Maple St…" became a 14-digit phone. A whole number in
      // it goes up to Phone (extractPhone — the same rule as the Name/address
      // box); everything else moves down, house number and all. What the
      // Phone box already held stays there.
      const prev=String(phoneQ||"");
      const kept=phoneDigits(prev).length;
      if(!kept||v.length-prev.length>1){
        const ex=extractPhone(v);
        if(ex){
          const next=join(ex.rest);
          setPhoneQ(formatPhoneInput(ex.phone));setTextQ(next);focusTextAtEnd(next);
          return;
        }
        // Nothing worth keeping up here (no digits, or a few with nothing
        // below yet — "4521" then the rest of a dictated address): it all
        // moves down, house number and all.
        if(!kept||(kept<7&&!cur.trim())){
          setPhoneQ("");const next=join(moved);setTextQ(next);focusTextAtEnd(next);
          return;
        }
        // Pasted into a number already there: the box keeps its own digits,
        // the pasted text moves down.
        let a=0;while(a<prev.length&&a<v.length&&prev.charAt(a)===v.charAt(a))a++;
        let z=0;while(z<prev.length-a&&z<v.length-a&&prev.charAt(prev.length-1-z)===v.charAt(v.length-1-z))z++;
        const pasted=v.slice(a,v.length-z);
        const down=lettersTypedInPhone(pasted);
        if(down!==null){
          const next=join(down);
          setPhoneQ(formatPhoneInput(prev.slice(0,a)+prev.slice(prev.length-z)));setTextQ(next);focusTextAtEnd(next);
          return;
        }
      }
      // Typed: count only the digits ahead of the letters — those are what
      // would stay up here (a zip later in the text never counts).
      const had=phoneDigits(splitPhoneTyping(v).digits).length;
      if(had<7&&!cur.trim()){
        // Short digits + letters ("4521 M") is an address started up here.
        setPhoneQ("");setTextQ(moved);focusTextAtEnd(moved);
        return;
      }
      // A number worth keeping stays in Phone; the letters join (or start)
      // the Name/address box — "(480) 555-1234" then "J" narrows by name.
      const sp=splitPhoneTyping(v);
      const next=!cur.trim()?sp.letters:(/\s$/.test(cur)?cur:cur+" ")+sp.letters;
      setPhoneQ(formatPhoneInput(sp.digits));setTextQ(next);focusTextAtEnd(next);
      return;
    }
    setPhoneQ(formatPhoneInput(v));
  }
  function onTextChange(v){
    if(!phoneDigits(phoneQ).length){
      const ex=extractPhone(v);
      if(ex){setPhoneQ(formatPhoneInput(ex.phone));setTextQ(ex.rest);return;}
    }
    setTextQ(v);
  }
  // The chip: name ⇄ address (⇄ name + address when both were typed).
  function flipKind(){
    const cur=textCls.kind;
    const next=nextKind(autoCls,cur);
    if(!next)return;
    setKindPick(next===autoCls.kind?null:{text:textQ,kind:next});
  }
  // Enter opens the first row that is what he typed (or what the phone found)
  // — never a close-match guess on its own: those take a click.
  function onFindKey(e){
    if(e.key!=="Enter")return;
    const r=results.find(function(x){return !(x.close&&x.via!=="both");});
    if(r){e.preventDefault();openResult(r);}
  }
  const upd=function(k,v){setTouched(true);setP(function(prev){var patch={};patch[k]=v;return Object.assign({},prev,patch);});};

  // ══════════════════════════════════════════════════════════════
  // NOTES SAVE THEMSELVES
  // ══════════════════════════════════════════════════════════════
  // Write one note entry through to the record right now — a note typed
  // mid-call must survive the browser being closed on the spot.
  function saveNoteEntry(t,quiet){
    const cur=pRef.current;
    if(!t||!cur)return null;
    const e=contactEntry("note",t);
    const persisted=ensurePersisted(cur);
    commit(persisted,[e]);
    writeContact(persisted,e);
    setP(function(prev){return prev?Object.assign({},prev,{kind:persisted.kind,id:persisted.id,contactLog:(prev.contactLog||[]).concat([e])}):prev;});
    setTouched(true);
    setSavedKey("note");
    if(!quiet)flashMsg("Note saved · "+e.date+" "+e.time);
    return e;
  }
  // The note box: Save note / ⌘-Enter, and on its own when the box loses
  // focus. takeText empties the draft as it reads it, so a blur followed by
  // the Save click (or a close) writes the note exactly once.
  function logNote(quiet){const t=takeText(noteDraftRef);setNoteDraftState("");saveNoteEntry(t,quiet);}
  // "What they're after" and the follow-up "About": saved ~0.7s after he stops
  // typing, and on blur / close / switching away. Only those two fields are
  // written (limitTo), so a half-typed phone or value isn't saved by it.
  const noteFieldWho=useRef("");
  function typeNoteField(k,v){
    upd(k,v);
    noteFieldsDirty.current=true;noteFieldWho.current=k;
    setSavedKey("");
    clearTimeout(noteFieldTimer.current);
    noteFieldTimer.current=setTimeout(saveNoteFields,700);
  }
  function saveNoteFields(){
    clearTimeout(noteFieldTimer.current);
    if(!noteFieldsDirty.current)return;
    const cur=pRef.current;
    // Not in the system yet: saved with the record when it's created.
    if(!cur||cur.kind==="new")return;
    noteFieldsDirty.current=false;
    commitRef.current(limitTo(cur,["goal","followUpNote"]),[]);
    setSavedKey(noteFieldWho.current);
  }
  // Everything typed that isn't on the record yet — the note box, a
  // disposition note that was never confirmed, the two free-text fields and
  // any other edit on the card. Run on close, on switching to someone else
  // and on unmount. A brand-new caller is only created for real text.
  function flushPending(){
    clearTimeout(noteFieldTimer.current);
    const cur=pRef.current;
    const ents=[];
    const t=takeText(noteDraftRef);if(t)ents.push(contactEntry("note",t));
    const dn=takeText(dispoNoteRef);if(dn)ents.push(contactEntry("note",dn));
    const fieldsTyped=noteFieldsDirty.current;noteFieldsDirty.current=false;
    if(!cur)return;
    if(!ents.length){
      if(cur.kind==="new"){if(!(fieldsTyped&&String(cur.goal||cur.followUpNote||"").trim()))return;}
      // Nothing new since the last write — or nothing typed and nothing priced at all.
      else if(draftSig(cur,sessionQuotes.current)===lastCommitSig.current)return;
      else if(!hasEdits(cur)&&!sessionQuotes.current.length)return;
    }
    const persisted=ensurePersisted(cur);
    commitRef.current(persisted,ents);
    ents.forEach(function(e){writeContact(persisted,e);});
  }
  const commitRef=useRef(commit);commitRef.current=commit;
  const flushRef=useRef(null);flushRef.current=flushPending;
  // Unmount (logout, idle sign-out) — the last chance to keep what was typed.
  useEffect(function(){return function(){if(flushRef.current)flushRef.current();};},[]);
  // Optional: App can ask for that same flush BEFORE it pushes its pending
  // saves on logout, so a note typed mid-call is in that push, not after it.
  useEffect(function(){
    if(!onRegisterFlush)return;
    onRegisterFlush(function(){if(flushRef.current)flushRef.current();});
    return function(){onRegisterFlush(null);};
  },[onRegisterFlush]);

  function setFollowUp(days,label){
    const d=isoDatePlus(days);
    setP(function(prev){return Object.assign({},prev,{followUpDate:d,followUpNote:prev.followUpNote||label||""});});
  }

  // ── Handwritten letter ──
  // The button only marks it; the save stamps the real send time. A lead gets
  // a proper `mailings` row (same shape Marketing logs, so its filters, touch
  // queue and mail stats count it). A pipeline file has no mailings of its
  // own — it gets a timestamped "mail" entry, and the lead it came from (if
  // any) gets the mailing row.
  function letterMailing(){
    const d=new Date();
    return{id:"ML-"+d.getTime(),envelope:"",returnAddr:"",stamp:"",method:"Handwritten",batch:"",campaign:"Handwritten Letter",
      dateSent:isoDatePlus(0),sentAt:d.toISOString(),notes:"",responded:false,respondedDate:""};
  }
  function addMailingTo(m,mailing){
    return{status:(!m.status||m.status==="New")?"Mailed":m.status,mailings:(m.mailings||[]).concat([mailing])};
  }
  // One click: save the lead (minting it if it's new — an address is enough)
  // and stamp the letter with the time it was confirmed.
  function confirmLetter(){
    const cur=pRef.current||p;
    if(!cur)return;
    const persisted=ensurePersisted(cur,true);
    const mailing=letterMailing();
    const e=contactEntry("mail","Handwritten letter sent",{mailingId:mailing.id});
    writeContact(persisted,e);
    if(persisted.kind==="loan"){
      const srcId=(persisted.raw&&persisted.raw.sourceLeadId)||persisted.viaLeadId||null;
      if(srcId)setMktgLeads(function(prev){return prev.map(function(m){return m.id===srcId?Object.assign({},m,addMailingTo(m,mailing)):m;});});
      commit(persisted,[e]);
    }else{
      commit(persisted,[e],null,function(m){return addMailingTo(m,mailing);});
    }
    setP(function(prev){return Object.assign({},prev,{kind:persisted.kind,id:persisted.id,statusLabel:persisted.statusLabel||prev.statusLabel,raw:persisted.raw||prev.raw,contactLog:(prev.contactLog||[]).concat([e])});});
    setSessionLetters(function(l){return l.concat([e.date+" "+e.time]);});
    setTouched(true);
    flashMsg("Handwritten letter logged · "+e.date+" "+e.time);
  }

  // "Save & close" — keep working it. No disposition, no pipeline file.
  function saveAndClose(){
    const cur=pRef.current||p;
    if(!cur){onClose();return;}
    const persisted=ensurePersisted(toFileIfConverted(cur));
    // A note still sitting in the box (or typed into a disposition he then
    // skipped) goes in with the save, not after it.
    const notes=[takeText(noteDraftRef),takeText(dispoNoteRef)].filter(Boolean).map(function(t){return contactEntry("note",t);});
    const entries=notes.concat(ranNumbers?[contactEntry("quote","Ran pricing — no pitch yet")]:[]);
    commit(persisted,entries);
    notes.forEach(function(e){writeContact(persisted,e);});
    if(callEpoch)setCallLog(markCallHandled(callEpoch,"saved"));
    finish("Saved. "+(persisted.name||"Lead")+" is still open — nothing moved.",true);
  }

  function goCalc(){
    const cur=pRef.current||p;
    if(!cur)return;
    // A lead that became a file since the card opened is priced AS the file,
    // so a pitch updates it instead of minting a duplicate.
    const persisted=ensurePersisted(toFileIfConverted(cur));
    commit(persisted,[]);
    setStep("calc");
  }

  // ── Disposition ──
  // Follow-up defaults differ by door: a qualified deal is a definite call-back,
  // a No App is a maybe (so it starts unset rather than pretending to a date),
  // and a denied deal usually isn't one at all.
  function askDispo(stage){
    setDispo({stage:stage});
    setDispoReason((FALLOUT_REASONS[stage]||[])[0]||"");
    setDispoNote("");
    setDispoFollowUp(stage===FALLOUT_QUAL?isoDatePlus(90):"");
    setDispoDnc(false);
  }
  // Switching door inside the open modal keeps what he already typed and the
  // do-not-contact tick — only the reason list (and its default) changes.
  function switchDispo(stage){
    setDispo({stage:stage});
    setDispoReason((FALLOUT_REASONS[stage]||[])[0]||"");
    setDispoFollowUp(function(prev){return stage===FALLOUT_QUAL?(prev||isoDatePlus(90)):prev;});
  }
  // Leaving the modal without confirming: the note typed in it is kept as an
  // ordinary note, never dropped.
  function leaveDispo(){
    const dn=takeText(dispoNoteRef);
    setDispoNoteState("");
    setDispo(null);
    if(dn)saveNoteEntry(dn);
  }
  // Picking "asked to be removed from mailings" and then NOT marking them
  // do-not-contact is how a compliance problem starts. Auto-tick it; he can
  // still untick if he misclicked.
  function pickReason(r){
    setDispoReason(r);
    if(/removed from mailings|do not contact/i.test(r))setDispoDnc(true);
  }
  // The fallout tag is what keeps these lists targetable from Marketing later
  // on — "everyone who called but never got priced" is a mailable segment.
  // Built from the LIVE row's tags (never the snapshot) so an owner change or
  // any other tag added meanwhile survives.
  const withFalloutTag=function(tags,stage){
    const tag=FALLOUT_TAGS[stage];
    return (tags||[]).filter(function(t){return t!=="fallout-qual"&&t!=="fallout-denied"&&t!=="fallout-noapp";}).concat(tag?[tag]:[]);
  };
  function confirmDispo(){
    const cur=pRef.current||p;
    if(!cur||!dispo)return;
    const stage=dispo.stage;
    const ts=new Date();
    // The modal's note is part of the disposition — taken here so the close-out
    // flush can't write it a second time.
    const dnote=takeText(dispoNoteRef);
    const text=stage+" — "+(dispoReason||"no reason given")+(dnote?" · "+dnote:"");
    // A lead that is already a file is dispositioned AS that file — never a
    // second file for the same person.
    const persisted=ensurePersisted(toFileIfConverted(cur));
    // A note still in the box lands just before the disposition, on the same record.
    const boxNote=takeText(noteDraftRef);
    const noteEnts=boxNote?[contactEntry("note",boxNote)]:[];
    const dispoEnt=contactEntry("disposition",text,{stage:stage,reason:dispoReason});
    const entries=noteEnts.concat([dispoEnt]);
    const draft=Object.assign({},persisted,{followUpDate:dispoFollowUp||persisted.followUpDate||""});

    if(draft.kind==="loan"){
      commit(draft,entries,{
        stage:stage,archived:true,queue:null,
        falloutReason:dispoReason||"",falloutAt:ts.toISOString(),
        followUpDate:dispoFollowUp||"",followUpNote:dnote||"",
      });
      noteEnts.forEach(function(e){writeContact(draft,e);});
      writeContact(draft,dispoEnt,{stage:stage,reason:dispoReason||""});
      // A pipeline file has no do-not-contact flag of its own — the mailing
      // list is what has to stop. The lead this file came from also joins
      // the fallout segment and points at the file.
      // Marketing → Promote stamps only lead.converted_loan_id (no
      // loan.sourceLeadId), so fall back to the lead that points at this file.
      const srcId=(draft.raw&&draft.raw.sourceLeadId)||draft.viaLeadId||null;
      if(srcId||draft.id){
        setMktgLeads(function(prev){return prev.map(function(m){
          if(srcId?m.id!==srcId:m.converted_loan_id!==draft.id)return m;
          return Object.assign({},m,{tags:withFalloutTag(m.tags,stage),converted_loan_id:m.converted_loan_id||draft.id,updated_at:ts.toISOString()},
            dispoDnc?{doNotContact:true,do_not_contact:true,status:"DNC"}:{});
        });});
      }
    }else{
      const loan=createLoanFromProspect(draft,stage,entries,{
        archived:true,
        falloutReason:dispoReason||"",falloutAt:ts.toISOString(),
        followUpDate:dispoFollowUp||"",followUpNote:dnote||"",
      });
      // The lead just became a file — carry this call's history across with it,
      // then log the disposition against the file itself.
      attachToLoan(draft.id,loan.id);
      const asFile=Object.assign({},draft,{kind:"loan",id:loan.id});
      noteEnts.forEach(function(e){writeContact(asFile,e);});
      writeContact(asFile,dispoEnt,{stage:stage,reason:dispoReason||""});
      // Keep the mail-list row honest and pointed at the new file.
      commit(draft,entries,null,function(m){
        return Object.assign({
          converted_loan_id:loan.id,
          status:dispoDnc?"DNC":stage===FALLOUT_DENIED?"Dead":"Responded",
          responded:true,
          responded_at:m.responded_at||ts.toISOString(),
          response_method:m.response_method||"Call",
          follow_up_date:dispoFollowUp||null,
          tags:withFalloutTag(m.tags,stage),
        },dispoDnc?{doNotContact:true,do_not_contact:true}:{});
      });
    }
    if(callEpoch)setCallLog(markCallHandled(callEpoch,stage));
    setDispo(null);setDispoNoteState("");
    finish(stage+" recorded for "+(draft.name||"this lead")+"."+
      (dispoDnc?" Marked do not contact.":" Everything from the call was saved."),true);
  }

  // Calc told us a scenario went to the pipeline. Patch the file it created or
  // updated with everything the call gathered, then close out.
  // `pitchedKind` = Calc's scenario type for the pitch ("first" | "refi" |
  // "hs2"); older callers leave it out and the file's last scenario says.
  // With `onOpenFile` (App) the console then closes on its own and opens the
  // deal (Reece, 10/10/26): nothing it holds may be lost on the way out, so a
  // note still in the box (or a disposition note) is taken HERE and lands on
  // the file just before the pitch line — the same record, in the same write —
  // and the close skips the draft flush (the draft is the pre-pitch snapshot;
  // everything it says is in this write — re-writing it could put a stale
  // value, type or deal type back over what Calc just pitched).
  function handlePitched(product,loanId,pitchedKind){
    const cur=pRef.current||p;
    if(!cur)return;
    const closing=!!onOpenFile;
    const pendingNotes=closing?[takeText(noteDraftRef),takeText(dispoNoteRef)].filter(Boolean).map(function(t){return contactEntry("note",t);}):[];
    // A pitch that overrode a lender rule (Calc's "Override benefit test", a
    // program that doesn't fit) says so on the file's history too.
    // (A HomeSafe Second priced in that mode is ALWAYS on FOA's amount — the
    // "lender amount" override is its input there, not news; the deal keeps it.)
    const overrides=(Array.isArray(product.overrides)?product.overrides.filter(Boolean):[]).filter(function(o){return !product.payOnly||o!==LENDER_AMOUNT_OVERRIDE;});
    // A HomeSafe Second pitched in that mode (PL in, pay out — Calc's
    // payOnly) has no costs / cash: the line says FOA's amount and the pay.
    const pitchWhat=product.payOnly
      ?$(product.gross)+" HomeSafe Second · your pay "+payCents(product.comp)
      :$(product.avail)+" available";
    const pitchEntry=contactEntry("pitch","Pitched "+product.name+" ("+product.lender+") — "+pitchWhat+(overrides.length?" — overrides: "+overrides.join("; "):""));
    const entries=pendingNotes.concat([pitchEntry]);
    const ts=new Date();
    const runs=sessionQuotes.current.slice();
    const base=baseProspect(cur);
    // A lead created on this very call: everything on it was typed on this call.
    const madeThisCall=!!(cur.raw&&cur.raw.created_at&&Date.parse(cur.raw.created_at)>=openedAt.current);
    const fuChanged=madeThisCall||!base||!sameVal(cur.followUpDate,base.followUpDate);
    // What this call changed, applied to the pitched file.
    //  • The file the card was opened on: exactly the fields edited on it.
    //  • A file Calc just minted from a lead: the card's details win. Calc
    //    built the file from what the card said when Run was clicked — a name,
    //    phone or address corrected beside the calculator after that is the
    //    one that's right.
    // Value, balance and product type are Calc's: it just wrote what was
    // pitched. And the follow-up only comes across if he changed it on this
    // call — a revived fallout file has just had its stale "call back when
    // rates move" cleared, and re-applying it would put a live deal straight
    // back on the call-back list.
    let patch;
    if(cur.kind==="loan"&&cur.id===loanId)patch=dirtyLoanPatch(cur);
    else{
      patch=loanPatchFrom(cur);
      ["b","ph","em","prop","dob","leadNotes"].forEach(function(k){if(!patch[k])delete patch[k];});
      if(!fuChanged){delete patch.followUpDate;delete patch.followUpNote;}
    }
    ["hv","mb","type","age"].forEach(function(k){delete patch[k];});
    if(patch.dob){const a=ageFromDobStr(patch.dob);if(a)patch.age=a;}
    let metaPatch=patch.leadMeta;delete patch.leadMeta;
    // A HomeSafe Second pitched is a HomeSafe Second file, whatever the card's
    // toggle said when Run was clicked (Calc stamped it the same way).
    if(product&&product._second)metaPatch=Object.assign({},metaPatch||{},{dealType:"hs2",isReverse:false});
    // …and a HECM / first-time product pitched off a card set to HS2 is that
    // deal, not a HomeSafe Second: the card's "hs2" never lands on the file
    // (Calc takes its own stamp off the same way) — it is set to what was
    // pitched, a refi → HECM, otherwise FTR.
    const hs2Undone=!(product&&product._second)&&!!metaPatch&&metaPatch.dealType==="hs2";
    setLoans(function(prev){return prev.map(function(l){
      if(l.id!==loanId)return l;
      let mp=metaPatch;
      if(hs2Undone){
        const h=Array.isArray(l.scenarioHistory)?l.scenarioHistory:[];
        const kind=pitchedKind||(h.length&&h[h.length-1]?h[h.length-1].dealKind:"");
        mp=Object.assign({},metaPatch,{dealType:kind==="refi"?"hecm":"ftr",isReverse:kind==="refi"});
      }
      return Object.assign({},l,patch,{
        leadMeta:Object.assign({},l.leadMeta||{},mp||{}),
        contactLog:(l.contactLog||[]).concat((cur.contactLog||[]).filter(function(e){
          // don't duplicate history already carried over by an earlier commit
          return !(l.contactLog||[]).some(function(x){return x.epoch===e.epoch;});
        })).concat(entries).slice(-CONTACT_LOG_MAX),
        // Every run of this call, merged — never a second copy of one.
        quoteLog:mergeQuotes(l.quoteLog,runs),
        activityLog:(l.activityLog||[]).concat(entries.map(toActivity)),
        lastContactAt:ts.toISOString(),
        sourceLeadId:l.sourceLeadId||(cur.kind==="lead"?cur.id:(cur.viaLeadId||null)),
        // A file Calc just minted is whoever pitched it; an existing one keeps its owner.
        owner:l.owner||staffKey,
      });
    });});
    if(cur.kind==="lead"){
      // The mail-list row gets the same corrections, points at the file, and
      // drops its own stale follow-up unless one was set on this call (the
      // file is what gets called back now).
      commit(cur,[],null,function(m){
        return Object.assign({status:"Consultation",converted_loan_id:loanId,responded:true,
          responded_at:m.responded_at||ts.toISOString(),response_method:m.response_method||"Call"},
          fuChanged?{}:{follow_up_date:null});
      });
    }
    // From here on the card IS the pitched file: notes, saves and a second
    // pitch go to it (see the re-base effect).
    rebaseAfterPitch.current={loanId:loanId,viaLeadId:cur.kind==="lead"?cur.id:(cur.viaLeadId||null),
      litigator:!!cur.litigator||isLitigatorLead(cur.raw),doNotContact:!!cur.doNotContact,
      lienAmount:num(cur.lienAmount),lienIsHecm:!!cur.lienIsHecm};
    // ── Durable record of the pitch ──
    // A pitch row is the single most valuable thing in the history: it is the
    // exact terms quoted, at the rate environment of that day. When the swap
    // moves, this is the row that says how much better the same house does now.
    if(cur.kind==="lead")attachToLoan(cur.id,loanId);
    logScenario(scenarioRow({
      loanId:loanId,leadId:cur.kind==="lead"?cur.id:(cur.viaLeadId||null),
      kind:"pitch",
      name:cur.name,phone:cur.phone,property:fullAddr(cur),
      product:product,
      inputs:{age:ageFromDobStr(cur.dob)||cur.age,dob:cur.dob,homeValue:num(cur.homeValue),
        mortgageBalance:num(cur.mortgageBalance),currentPL:num(cur.curPL)||null,
        currentAvail:num(cur.curAvail)||null,closingCosts:product.userCC||null,
        existingLoan:pitchedKind==="refi"&&cur.existingLoan?cur.existingLoan:null},
      productCount:lastQuote?lastQuote.productCount:null,
      allProducts:(lastQuote&&lastQuote.top)||[],
      rateEnv:rateEnvironment(rateSheets),
      note:cur.goal||"",
    }));
    const asFile=Object.assign({},cur,{kind:"loan",id:loanId});
    pendingNotes.forEach(function(e){writeContact(asFile,e);});
    writeContact(asFile,pitchEntry,{stage:"Pitched"});

    if(callEpoch)setCallLog(markCallHandled(callEpoch,"pitched"));
    if(closing){
      // Everything is in the writes above — close (no disposition: the pitch
      // is the outcome) and open the deal.
      finish("Pitched to pipeline — "+loanId,true);
      onOpenFile(loanId);
      return;
    }
    setPitched({product:product.name,loanId:loanId});
  }

  // ── Every pricing run is recorded, pitched or not ──
  // This is the half of the history that used to vanish entirely: the deals
  // that got priced and walked away from. Each run of the call is kept in
  // sessionQuotes and merged into the record's quote log on the next write
  // (an identical repeat isn't kept twice). `bestForBorrower` — not the
  // highest-comp product — is what goes on the scenarios row, because that is
  // the number that was actually said out loud on the call.
  function handleQuoteRun(snapshot,products,inputs){
    setLastQuote(snapshot);
    setRanNumbers(true);
    if(snapshot){const q=sessionQuotes.current;if(!q.length||!sameRun(q[q.length-1],snapshot))q.push(snapshot);}
    // What was priced is what the record says: a value, balance or DOB typed
    // into the calculator goes back onto the card, so a save or a disposition
    // writes the numbers that were actually run — a lien lead priced with its
    // real balance no longer comes back reading "Owes $0".
    const inp=inputs||{};
    setP(function(prev){
      if(!prev)return prev;
      const patch={};
      const hv=num(inp.homeValue),mb=num(inp.mortgageBalance);
      if(hv>0&&hv!==num(prev.homeValue))patch.homeValue=hv;
      if(inp.mortgageBalance!=null&&mb!==num(prev.mortgageBalance))patch.mortgageBalance=mb;
      if(inp.dob&&inp.dob!==prev.dob&&!(!prev.dob&&isAgePlaceholderDob(inp.dob,prev.age)))patch.dob=inp.dob;
      if(prev.isReverse){
        // The calculator's refi inputs are the balance + current principal
        // limit only (10/2/26 — no current-available / LOC box any more).
        if(num(inp.currentPL)>0&&num(inp.currentPL)!==num(prev.curPL))patch.curPL=String(num(inp.currentPL));
        if(num(inp.closingCosts)>0&&num(inp.closingCosts)!==num(prev.estCC))patch.estCC=String(num(inp.closingCosts));
      }
      // A refi run says how the existing reverse was read (Adjustable /
      // Fixed). The card keeps the pick, so ← Edit the deal → Run the Numbers
      // reopens on it and a save puts it on the record (leadMeta / the
      // lead's profile meta — calcRules.existingLoanChoice reads it back).
      if((inp.existingLoan==="fixed"||inp.existingLoan==="adjustable")&&inp.existingLoan!==prev.existingLoan)patch.existingLoan=inp.existingLoan;
      return Object.keys(patch).length?Object.assign({},prev,patch):prev;
    });
    const cur=pRef.current||p;
    if(!cur||!cur.id)return;
    const best=(products||[]).slice().sort(function(a,b){return (b.avail||0)-(a.avail||0);})[0];
    if(!best)return;
    logScenario(scenarioRow({
      loanId:cur.kind==="loan"?cur.id:null,
      leadId:cur.kind==="lead"?cur.id:(cur.viaLeadId||null),
      kind:"quote",
      name:cur.name,phone:cur.phone,property:fullAddr(cur),
      product:best,inputs:inputs||{},
      productCount:(products||[]).length,
      allProducts:(snapshot&&snapshot.top)||[],
      rateEnv:rateEnvironment(rateSheets),
    }));
  }

  // Close the console. `saved` = the caller just wrote everything itself (a
  // disposition, Save & close); any other close — Done after a pitch, the X,
  // Calc navigating away — first keeps whatever is still typed.
  function finish(msg,saved){
    if(!saved)flushPending();
    clearTimeout(noteFieldTimer.current);noteFieldsDirty.current=false;
    noteDraftRef.current="";dispoNoteRef.current="";setNoteDraftState("");setDispoNoteState("");
    pRef.current=null;sessionQuotes.current=[];rebaseAfterPitch.current=null;lastCommitSig.current="";
    setP(null);setStep("find");setPhoneQ("");setTextQ("");setKindPick(null);setSavedKey("");
    setLastQuote(null);setRanNumbers(false);setPitched(null);setCallEpoch(null);setTouched(false);setSessionLetters([]);
    onClose(msg||"");
  }

  // Closing a worked call without a disposition is exactly the moment things
  // used to evaporate — ask, don't assume. Which door it opens on depends on
  // what actually happened: priced-but-not-pitched defaults to Qualified,
  // while a call that never got priced defaults to No App, because that is the
  // only one of the three that is true when no numbers exist.
  function requestClose(){
    if(p&&ranNumbers&&!pitched){askDispo(FALLOUT_QUAL);return;}
    // Only prompt on a call that was actually worked — a lead he opened, typed
    // into, and is now closing. Just looking someone up shouldn't demand a
    // disposition.
    if(p&&!pitched&&touched){askDispo(FALLOUT_NOAPP);return;}
    // A note still in the box is saved by the close itself (finish → flushPending).
    finish("");
  }

  // ══════════════════════════════════════════════════════════════
  // QUICK LISTS (shown before he types anything)
  // ══════════════════════════════════════════════════════════════
  // Computed above the `open` guard — hooks must run on every render, in the
  // same order, or React tears the component down.
  const today=isoDatePlus(0);
  const dueFollowUps=useMemo(function(){
    return open?collectDueFollowUps(loans,mktgLeads,today):[];
  },[open,loans,mktgLeads,today]);

  if(!open)return null;

  const waitingCallIns=loans.filter(function(l){return !l.archived&&l.queue==="callin";}).slice(0,12);
  const recentCalls=callLog.slice(0,12);

  // The LIVE pipeline file behind this prospect, if one exists. `p.raw` is a
  // snapshot taken when the card opened, so reading commission/deal off it
  // shows stale numbers the moment a scenario is pitched mid-call. Resolve it
  // out of the loans array on every render instead.
  const liveLoanId=(pitched&&pitched.loanId)||(p&&p.kind==="loan"?p.id:(p&&p.convertedLoanId)||null);
  const liveLoan=liveLoanId?(loans.find(function(l){return l.id===liveLoanId;})||(p&&p.raw)):null;
  // Same for a lead: the live mail-list row (owner tag, litigator tag) — the
  // snapshot doesn't see a reassignment made a second ago.
  const liveLead=p&&p.kind==="lead"?((mktgLeads||[]).find(function(m){return m.id===p.id;})||p.raw):null;
  // Skip-trace litigator flag on this caller — information only (it keeps the
  // lead off the Outbound dialer); click-to-call works like for anyone. The
  // tag is the app's `tcpa-litigator` or the list sync's `litigator`
  // (convoGuide hasLitigatorTag — the same pair the On file block reads).
  const pLit=!!(p&&(p.litigator||isLitigatorLead(p.raw)||isLitigatorLead(liveLead)||hasLitigatorTag(p.raw)||hasLitigatorTag(liveLead)));
  // "On file — what we know" (src/convoGuide.js + LeadFacts, 10/10/26): read
  // off the mail-list row behind this card — the lead itself, or the lead a
  // pipeline file came from when it's in memory. Read-only; it never writes,
  // suggests or blocks anything.
  const guideSrcId=p&&p.kind==="loan"?((liveLoan&&liveLoan.sourceLeadId)||p.viaLeadId||null):null;
  const guideLead=p&&p.kind==="lead"?liveLead:guideSrcId?((mktgLeads||[]).find(function(m){return m.id===guideSrcId;})||null):null;
  // The card's own loan chips (rate, lender, originated, loan type) that the
  // first-mortgage line already says — not printed twice. A value changed on
  // the card ("Correct something") differs, so it still shows.
  const chipCover=guideLead&&p?factsCover(leadFactsFor(guideLead),{rate:p.curRate,lender:p.lender,origDate:p.origDate,loanType:p.loanType}):{};

  // ── Owner ──
  // Files: loan.owner (none yet = counts as Reece's, like everywhere else).
  // Leads: the "owner:<key>" tag. A brand-new caller: whoever is on the call,
  // unless another owner is picked before it's saved.
  const ownerInfo=!p?null
    :p.kind==="loan"?{owner:(liveLoan&&liveLoan.owner)||null,dflt:ownerOf(liveLoan)}
    :p.kind==="lead"?{owner:leadOwner(liveLead),dflt:null}
    :{owner:p.owner||staffKey,dflt:null,note:" · when saved"};
  const setOwner=function(k){
    if(!p||(k!=="reece"&&k!=="max"))return;
    if(p.kind==="new"){setP(function(prev){return Object.assign({},prev,{owner:k});});return;}
    if(p.kind==="loan"){
      setLoans(function(prev){return prev.map(function(l){return l.id===p.id?Object.assign({},l,{owner:k}):l;});});
      // The mail-list row it came from follows, so Marketing's Mine/Max agrees.
      const srcId=(liveLoan&&liveLoan.sourceLeadId)||p.viaLeadId;
      if(srcId)setMktgLeads(function(prev){return prev.map(function(m){return m.id===srcId?withLeadOwner(m,k):m;});});
    }else{
      setMktgLeads(function(prev){return prev.map(function(m){return m.id===p.id?withLeadOwner(m,k):m;});});
    }
    flashMsg((p.name||"This "+(p.kind==="loan"?"file":"lead"))+" now belongs to "+staffName(k)+".");
  };
  const ownerChip=ownerInfo&&ownershipUi?<OwnerChip owner={ownerInfo.owner} dflt={ownerInfo.dflt} note={ownerInfo.note} onSet={setOwner}/>:null;

  const kindBadge=function(r){
    if(r.kind==="loan"){
      const c=STC[r.status]||C.accent;
      return <Badge label={r.archived?"Fallout · "+r.status:r.status} color={c} bg={c+"18"}/>;
    }
    // A lead that already became a file opens AS that file — say so up front.
    const file=r.raw?convertedLoanFor(r.raw,loans):null;
    if(file)return <Badge label={"Already a file · "+file.id} color={C.accent} bg={C.accent+"18"}/>;
    return <Badge label={r.status||"Lead"} color={C.cyan} bg={C.cyan+"18"}/>;
  };
  // How a row was found, once a number is in the Phone box.
  const textWord=searchCls.kind==="address"?"address":searchCls.kind==="name"?"name":"name/address";
  const viaBadge=function(r){
    if(r.via==="both")return <Badge label={"Phone + "+textWord} color={C.green} bg={C.greenBg}/>;
    if(r.via==="phone")return <Badge label="Phone match" color={C.green} bg={C.greenBg}/>;
    return <Badge label={textWord.charAt(0).toUpperCase()+textWord.slice(1)+" match"} color={C.textMuted} bg={C.surfaceAlt}/>;
  };
  // Not exactly what was typed (a spelling, street type or direction is off)
  // — always listed under the exact matches. With a number in the Phone box,
  // a close match the phone also found says so.
  const closeBadge=function(r){
    return <span className="cc-close" title="Not an exact match — a spelling, the street type or the direction differs from what you typed">
      <Badge label={r.via==="both"?"Phone + close match":"Close match"} color={C.gold} bg={C.goldBg}/>
    </span>;
  };
  const textExactShown=exactShown-phoneOnlyShown;
  const resultsHeading=results.length===0?(remote.loading?"Searching the full database…":"No matches")
    :closeShown?(textExactShown?textExactShown+" match"+(textExactShown===1?"":"es"):"No exact match")+" · "+closeShown+" close match"+(closeShown===1?"":"es")
      +(phoneOnlyShown?" · "+phoneOnlyShown+" by phone only":"")
    :results.length+" match"+(results.length===1?"":"es");
  // The new-lead buttons say what they will fill in — a partial number too,
  // so it never disappears without a word.
  const newLeadFill=(function(){
    const what=newLeadSummary(textCls);
    const partial=typedPhone?"":partialPhoneForLead(phoneQ);
    const ph=typedPhone?fmtPhone10(typedPhone)
      :partial?partial+(phoneDigits(phoneQ).length<10?" (incomplete)":""):"";
    return [what,ph].filter(Boolean).join(" · ");
  })();
  const newLeadButtonLabel="+ Start a new lead"+(newLeadFill?" — "+newLeadFill:"");

  // Right-hand column: contact, notes, follow-up, history. Rendered beside
  // the lead card AND beside the calculator, so running numbers never takes
  // the contact info or notes off screen.
  // Handwritten letters already on record, as display strings, oldest first.
  // Only the "Handwritten Letter" campaign counts — the same rule as the
  // Letters tracker. Mailhouse batches also carry method "Handwritten" (the
  // Marketing mail form's default), and counting those said "Sent 1×" for a
  // letter that was never written. They're shown apart, as batch mailings.
  const leadMailings=!p||p.kind!=="lead"?[]:((p.raw&&p.raw.mailings)||[]);
  const lettersSent=!p?[]:p.kind==="lead"
    ?leadMailings.filter(function(m){return m&&m.campaign===LETTER_CAMPAIGN;}).map(function(m){
      return m.sentAt?new Date(m.sentAt).toLocaleDateString()+" "+new Date(m.sentAt).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}):(m.dateSent||"");})
    :(p.contactLog||[]).filter(function(e){return e&&e.type==="mail";}).map(function(e){return e.date+" "+e.time;});
  const batchMailed=leadMailings.filter(function(m){return m&&m.campaign!==LETTER_CAMPAIGN;}).length;
  // Loans already carry this visit's letters in contactLog; leads add them here.
  if(p&&p.kind!=="loan")sessionLetters.forEach(function(x){lettersSent.push(x);});
  const sidePanel=p?(
    <div style={{display:"flex",flexDirection:"column",gap:16}}>

      {/* Contact — who they are and how to reach them, same for HECM and FTR */}
      <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:16,padding:"18px 20px"}}>
        <SectionTitle first>Contact</SectionTitle>
        {p.phone&&<div style={{marginBottom:12}}><CallBtn phone={p.phone}/></div>}
        {pLit&&<div className="cc-flag-note" style={{marginTop:-4,marginBottom:12,fontSize:11.5,color:C.textMuted}}>ⓘ {CALLED_IN_FLAG_NOTE}. Kept off the Outbound dialer.</div>}
        <div style={{display:"flex",flexWrap:"wrap",gap:12}}>
          <Field label="Name" wide><Inp value={p.name||""} onChange={function(e){upd("name",e.target.value);}} placeholder="Full name"/></Field>
          <Field label="Phone"><Inp value={p.phone||""} onChange={function(e){upd("phone",e.target.value);}} placeholder="(602) 555-1234"/></Field>
          <Field label="Email"><Inp value={p.email||""} onChange={function(e){upd("email",e.target.value);}} placeholder="email@example.com"/></Field>
          {p.kind==="loan"?
            <Field label="Property address" wide><Inp value={p.address||""} onChange={function(e){upd("address",e.target.value);}} placeholder="Full address"/></Field>
          :<>
            <Field label="Street" wide><Inp value={p.street||""} onChange={function(e){upd("street",e.target.value);}} placeholder="4521 Maple Ridge Dr"/></Field>
            <Field label="City" basis="40%"><Inp value={p.city||""} onChange={function(e){upd("city",e.target.value);}} placeholder="Phoenix"/></Field>
            <Field label="State" basis="20%" min={70}><Inp value={p.state||""} onChange={function(e){upd("state",e.target.value);}} placeholder="AZ"/></Field>
            <Field label="Zip" basis="25%" min={80}><Inp value={p.zip||""} onChange={function(e){
              const v=e.target.value;upd("zip",v);
              // New leads only: fill City/State from the zip, never over anything typed.
              if(p.kind==="new")lookupZip(v).then(function(hit){
                if(!hit)return;
                setP(function(prev){
                  if(!prev||prev.kind!=="new"||String(prev.zip||"").replace(/[^0-9]/g,"").slice(0,5)!==String(v).replace(/[^0-9]/g,"").slice(0,5))return prev;
                  return Object.assign({},prev,{city:prev.city||hit.city,state:prev.state||hit.state});
                });
              });
            }} placeholder="85018"/></Field>
          </>}
        </div>
        {/* Mail — one click saves the lead and stamps the letter with the time. */}
        <div style={{marginTop:14,paddingTop:14,borderTop:"1px solid "+C.border,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <Btn small onClick={saveAndClose}>Save Lead</Btn>
          <button onClick={confirmLetter} style={{padding:"9px 14px",borderRadius:9,cursor:"pointer",fontSize:12.5,fontWeight:800,fontFamily:"'Mulish',system-ui,sans-serif",
            border:"1px solid "+C.clay,background:C.clay,color:"#fff"}}>✉ Confirm Handwritten Letter</button>
          <span style={{fontSize:11.5,color:C.textDim}}>
            {lettersSent.length?"Sent "+lettersSent.length+"× · last "+lettersSent[lettersSent.length-1]:"No handwritten letters yet"}
            {batchMailed>0?" · mailed "+batchMailed+"× by batch":""}
          </span>
        </div>
      </div>

      <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:16,padding:"18px 20px"}}>
        <SectionTitle first right={<SavedHint show={savedKey==="note"&&!noteDraft}/>}>Log a note</SectionTitle>
        <textarea value={noteDraft} onChange={function(e){setNoteDraft(e.target.value);if(savedKey==="note")setSavedKey("");}}
          onKeyDown={function(e){if(e.key==="Enter"&&(e.metaKey||e.ctrlKey))logNote();}}
          onBlur={function(){if(noteDraftRef.current.trim())logNote();}}
          placeholder="What was said. Saves on its own with today's date and time."
          style={{width:"100%",minHeight:78,padding:"11px 14px",borderRadius:8,border:"1px solid "+C.border,background:C.surfaceAlt,color:C.text,fontSize:13,fontFamily:"'Mulish',system-ui,sans-serif",resize:"vertical",outline:"none",boxSizing:"border-box"}}/>
        <div style={{display:"flex",alignItems:"center",gap:10,marginTop:10}}>
          <Btn small primary onClick={function(){logNote();}} disabled={!noteDraft.trim()}>Save note</Btn>
          <span style={{fontSize:10.5,color:C.textDim}}>⌘/Ctrl + Enter · or just click away</span>
        </div>
      </div>

      <div style={{background:C.card,border:"1px solid "+(p.followUpDate?C.orange+"55":C.border),borderRadius:16,padding:"18px 20px"}}>
        <SectionTitle first>Follow up</SectionTitle>
        <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:11}}>
          {[{d:1,l:"Tomorrow"},{d:3,l:"3 days"},{d:7,l:"1 week"},{d:30,l:"30 days"},{d:90,l:"90 days"},{d:180,l:"6 months"}].map(function(o){
            const on=p.followUpDate===isoDatePlus(o.d);
            return <button key={o.d} onClick={function(){setFollowUp(o.d,"");}} style={{padding:"5px 11px",borderRadius:20,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif",
              border:"1px solid "+(on?C.orange:C.border),background:on?C.orangeBg:"transparent",color:on?C.orange:C.textMuted}}>{o.l}</button>;
          })}
        </div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          <Field label="Date" basis="45%" min={140}><Inp type="date" value={p.followUpDate||""} onChange={function(e){upd("followUpDate",e.target.value);}} style={{colorScheme:"light"}}/></Field>
          <Field label={<>About <SavedHint show={savedKey==="followUpNote"}/></>} basis="50%" min={150}><div onBlur={saveNoteFields}><Inp value={p.followUpNote||""} onChange={function(e){typeNoteField("followUpNote",e.target.value);}} placeholder="Call back after the sale closes"/></div></Field>
        </div>
        {p.followUpDate&&<div style={{marginTop:9,display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:11.5,color:C.orange,fontWeight:700}}>Due {p.followUpDate}</span>
          <Btn small onClick={function(){upd("followUpDate","");upd("followUpNote","");}} style={{color:C.textDim,fontSize:10.5}}>Clear</Btn>
        </div>}
      </div>

      <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:16,padding:"18px 20px"}}>
        <SectionTitle first>History</SectionTitle>
        {/* The list notes minus the lines "On file — what we know" already
            shows (notesWithoutFacts) — each fact and the fit once per card. */}
        <Timeline contactLog={p.contactLog} quoteLog={p.quoteLog} notes={guideLead?notesWithoutFacts(p.notes,guideLead):p.notes}
          dbScenarios={history.scenarios} dbContacts={history.contacts} dbLoaded={history.loaded}/>
      </div>
    </div>
  ):null;

  return(<div style={{position:"fixed",inset:0,zIndex:400,background:C.bg,display:"flex",flexDirection:"column",fontFamily:"'Mulish',system-ui,sans-serif"}}>

    {/* ── Console header ── */}
    <div style={{flexShrink:0,display:"flex",alignItems:"center",gap:14,padding:"14px 24px",background:C.navyDeep,color:C.cream,borderBottom:"1px solid rgba(242,239,231,0.10)"}}>
      <span style={{display:"inline-flex",width:34,height:34,borderRadius:9,background:C.clay,color:"#fff",alignItems:"center",justifyContent:"center",flexShrink:0}}>{Ic.phone}</span>
      <div style={{minWidth:0}}>
        <div style={{fontSize:15,fontWeight:800,letterSpacing:.2}}>New Call</div>
        <div style={{fontSize:10.5,color:"rgba(242,239,231,0.6)"}}>
          {step==="find"?"Their phone — then their name or address":
           step==="card"?"Confirm what you know — then run the numbers":
           "Price it, then pitch it or fall it out"}
        </div>
      </div>
      {/* breadcrumb */}
      <div style={{display:"flex",alignItems:"center",gap:6,marginLeft:18}}>
        {[{k:"find",l:"1 · Find"},{k:"card",l:"2 · The Lead"}].map(function(s,i){
          // Lead card and numbers are one screen now — both light up step 2.
          const cur=step==="calc"?"card":step;
          const active=cur===s.k;
          const done=(["find","card"].indexOf(cur)>i);
          return <span key={s.k} style={{fontSize:10.5,fontWeight:active?800:600,padding:"4px 11px",borderRadius:20,
            background:active?C.clay:done?"rgba(242,239,231,0.14)":"transparent",
            color:active?"#fff":done?C.cream:"rgba(242,239,231,0.45)",
            border:"1px solid "+(active?C.clay:"rgba(242,239,231,0.14)")}}>{s.l}</span>;
        })}
      </div>
      <div style={{flex:1}}/>
      {p&&<div style={{textAlign:"right",minWidth:0,marginRight:6}}>
        <div style={{fontSize:13,fontWeight:700,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:280}}>{p.name||"(new caller)"}</div>
        <div style={{fontSize:10.5,color:"rgba(242,239,231,0.6)"}}>{p.kind==="loan"?"Pipeline file "+p.id:p.kind==="lead"?"Lead "+p.id:"Not in the system yet"}</div>
      </div>}
      <button onClick={requestClose} title="Close (Esc)" style={{width:34,height:34,borderRadius:9,border:"1px solid rgba(242,239,231,0.22)",background:"transparent",color:C.cream,cursor:"pointer",fontSize:16,flexShrink:0}}>✕</button>
    </div>

    {flash&&<div style={{flexShrink:0,padding:"9px 24px",background:C.greenBg,borderBottom:"1px solid "+C.green+"35",color:C.green,fontSize:12.5,fontWeight:700}}>{flash}</div>}

    <div style={{flex:1,overflowY:"auto"}}>

      {/* ══════════════ STEP 1 — FIND ══════════════ */}
      {step==="find"&&<div className="cc-find">
        <style>{FIND_CSS}</style>
        {/* Phone first — the number he's on. */}
        <label htmlFor="cc-find-phone" style={FIND_LABEL}>Phone</label>
        <div style={{position:"relative",marginBottom:14}}>
          <span style={FIND_ICON}>{Ic.phone}</span>
          <input id="cc-find-phone" ref={phoneRef} className="cc-find-input cc-phone" type="tel" inputMode="tel" autoComplete="off" autoFocus
            value={phoneQ} onChange={function(e){onPhoneChange(e.target.value);}} onKeyDown={onFindKey}
            placeholder="(480) 555-1234"
            style={Object.assign({},FIND_BOX,{border:"2px solid "+(phoneQ?C.accent:C.border),fontWeight:700})}/>
          {phoneQ&&<button type="button" aria-label="Clear phone" onClick={function(){setPhoneQ("");if(phoneRef.current)phoneRef.current.focus();}} style={FIND_CLEAR}>×</button>}
        </div>
        {/* Then who or where — read as a name or an address (the chip says which). */}
        <label htmlFor="cc-find-text" style={FIND_LABEL}>Name or address</label>
        <div style={{position:"relative",marginBottom:8}}>
          <span style={FIND_ICON}>{Ic.search}</span>
          <input id="cc-find-text" ref={textRef} className="cc-find-input" type="text" autoComplete="off"
            value={textQ} onChange={function(e){onTextChange(e.target.value);}} onKeyDown={onFindKey}
            placeholder="Mary Jones — or 4521 N Maple St, Mesa AZ"
            style={Object.assign({},FIND_BOX,{border:"2px solid "+(textQ?C.accent:C.border),fontWeight:600})}/>
          {textQ&&<button type="button" aria-label="Clear name or address" onClick={function(){setTextQ("");setKindPick(null);if(textRef.current)textRef.current.focus();}} style={FIND_CLEAR}>×</button>}
        </div>
        {textQ.trim()&&textCls.kind!=="unknown"&&<div style={{marginBottom:10}}>
          <button type="button" data-kind={textCls.kind} className="cc-kind-chip" onClick={flipKind}
            title="Wrong? Click to switch between name and address — a new lead is filled in the same way"
            style={{display:"inline-flex",alignItems:"center",flexWrap:"wrap",gap:"2px 8px",maxWidth:"100%",textAlign:"left",padding:"6px 12px",borderRadius:20,cursor:"pointer",
              border:"1px solid "+(textCls.forced?C.accent:C.border),background:textCls.forced?C.accent+"12":C.surfaceAlt,fontSize:12.5,lineHeight:1.4,fontFamily:"'Mulish',system-ui,sans-serif",color:C.text}}>
            <span style={{color:C.textDim,fontWeight:600}}>Searching as:</span>
            <span style={{fontWeight:800,minWidth:0,overflowWrap:"anywhere"}}>{kindLabel(textCls)}</span>
            <span style={{color:C.accentLight,fontWeight:800}}>⇄ switch</span>
          </button>
        </div>}
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:22,flexWrap:"wrap"}}>
          <span style={{fontSize:11,color:C.textDim}}>
            Searches your pipeline files and every marketing lead{allLeadsLoaded===false?" (never-mailed ones are looked up in the database)":""}. Enter opens the top match.
          </span>
          {!leadsReady&&<span style={{fontSize:11,fontWeight:700,color:C.orange,background:C.orangeBg,padding:"3px 10px",borderRadius:20}}>
            Marketing database still loading{leadLoadProgress?" — "+leadLoadProgress.loaded.toLocaleString()+" of "+(leadLoadProgress.total||"?").toLocaleString():""}…
          </span>}
        </div>

        {/* Skip-trace phone match — suggestion only */}
        {phoneMatches.length>0&&<div style={{marginBottom:16,display:"flex",flexDirection:"column",gap:8}}>
          {phoneMatches.map(function(m){return(
            <div key={m.contact.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderRadius:12,background:C.goldBg||C.orangeBg,border:"1px solid "+(C.gold||C.orange)+"55",flexWrap:"wrap"}}>
              <span style={{fontSize:18}}>📇</span>
              <div style={{flex:1,minWidth:200}}>
                <div style={{fontSize:13.5,fontWeight:800,color:C.text}}>This number is on the skip-trace list for {m.lead.name||m.contact.name}</div>
                <div style={{fontSize:11.5,color:C.textMuted}}>{[m.lead.address,m.lead.city,m.lead.state].filter(Boolean).join(", ")} · {m.lead.id}{m.phone&&m.phone.type?" · "+m.phone.type:""}{(m.phone&&m.phone.dnc)||phoneMatchIsLitigator(m)?" · on the skip-trace DNC/litigator list (they called you, so calling back is fine)":""}</div>
              </div>
              <Btn small primary onClick={function(){openPhoneMatch(m);}}>Open this lead →</Btn>
            </div>
          );})}
        </div>}

        {/* Phone typed: found / looking / nothing (→ type the name or address) */}
        {typedPhone&&<PhoneStatus phone={fmtPhone10(typedPhone)} searching={phoneSearching} found={foundByPhone}
          hasText={!!textQ.trim()} flagged={typedIsFlagged} onStartNew={startNew}/>}

        {/* Results */}
        {(results.length>0||textQ.trim().length>=2||(phoneDigits(phoneQ).length>=4&&!typedPhone))&&<div style={{marginBottom:26}}>
          <div style={{fontSize:11,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.7,marginBottom:10}}>
            {resultsHeading}{remote.loading&&results.length>0?" · still checking the full database…":""}
          </div>
          {results.length===0?
            <div style={{padding:"26px 20px",textAlign:"center",border:"1px dashed "+C.border,borderRadius:12,color:C.textDim,fontSize:13}}>
              Nobody in the pipeline or the marketing database matches “{textQ.trim()||phoneQ.trim()}”.
              <div style={{marginTop:14}}><Btn primary onClick={startNew}>{newLeadButtonLabel}</Btn></div>
            </div>:
            <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,overflow:"hidden"}}>
              {results.map(function(r,i){
                return(<div key={r.kind+"-"+r.id} className="cc-row" data-via={r.via} data-close={r.close?"1":undefined} onClick={function(){openResult(r);}}
                  style={{display:"flex",alignItems:"center",gap:14,padding:"12px 16px",cursor:"pointer",borderBottom:i<results.length-1?"1px solid "+C.borderLight:"none"}}
                  onMouseEnter={function(e){e.currentTarget.style.background=C.cardHover;}}
                  onMouseLeave={function(e){e.currentTarget.style.background="transparent";}}>
                  <div style={{width:34,height:34,borderRadius:"50%",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,
                    background:(r.kind==="loan"?C.accent:C.cyan)+"18",color:r.kind==="loan"?C.accent:C.cyan}}>{(r.name||"?").charAt(0).toUpperCase()}</div>
                  <div className="cc-row-main">
                    <div style={{fontSize:14,fontWeight:700,color:C.text}}>{r.name}</div>
                    <div style={{fontSize:11.5,color:C.textDim,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                      {r.sub||"no address on file"} · {r.ph||"no phone"} · {r.id}
                    </div>
                  </div>
                  {/* With a number in the Phone box, say how each row was found;
                      a close match always says so. On a phone the badges drop
                      to their own line under the name. */}
                  <span className="cc-row-badges">
                    {r.close?closeBadge(r):phoneSearch&&viaBadge(r)}
                    {kindBadge(r)}
                  </span>
                  <span className="cc-open-hint" style={{fontSize:11.5,fontWeight:700,color:C.accentLight,flexShrink:0}}>Open →</span>
                </div>);
              })}
              <div style={{padding:"10px 16px",borderTop:"1px solid "+C.borderLight,background:C.surfaceAlt}}>
                <Btn small onClick={startNew} style={{color:C.accentLight}}>+ None of these — start a new lead{newLeadFill?": "+newLeadFill:""}</Btn>
              </div>
            </div>}
        </div>}

        {/* Quick lists */}
        {!phoneQ.trim()&&!textQ.trim()&&<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(300px,100%),1fr))",gap:16}}>

          {/* Calls to return (10/10/26, src/views/Callbacks.jsx): the
              account-wide list from RingCentral's call log — missed calls and
              voicemails not returned yet, longest wait first — when it can be
              read; this device's "Rang your line" ring buffer otherwise. */}
          <CallbacksCard staff={staffKey} settings={settings} rcAuthed={rcAuthed} loans={loans} mktgLeads={mktgLeads}
            allLeadsLoaded={allLeadsLoaded} practice={practice}
            onOpen={function(row,link){
              if(link&&link.kind==="loan"&&link.loan){openProspect(prospectFromLoan(link.loan));return;}
              if(link&&link.kind==="lead"&&link.lead){
                if(adoptLeads&&!(mktgLeads||[]).some(function(x){return x&&x.id===link.lead.id;}))adoptLeads([link.lead]);
                openProspect(prospectFromLead(link.lead));return;
              }
              setPhoneQ(formatPhoneInput(phoneDigits(row.number||"")));setTextQ("");setKindPick(null);
            }}
            fallback={<QuickCard title="Rang your line" color={C.clay} count={recentCalls.length}
            empty="No inbound calls logged on this device yet. They land here the moment RingCentral rings.">
            {recentCalls.map(function(c){
              return(<QuickRow key={c.epoch} onClick={function(){
                  setCallEpoch(c.epoch);
                  if(c.matchKind==="loan"){var l=loans.find(function(x){return x.id===c.matchId;});if(l){openProspect(prospectFromLoan(l),c.epoch);return;}}
                  if(c.matchKind==="lead"){var m=mktgLeads.find(function(x){return x.id===c.matchId;});if(m){openProspect(prospectFromLead(m),c.epoch);return;}}
                  setPhoneQ(formatPhoneInput(phoneDigits(c.from||"")));setTextQ("");setKindPick(null);
                }}
                title={c.matchName||c.fromName||c.from||"Unknown number"}
                sub={(c.from||"")+" · "+fmtWhen(c.iso)}
                right={c.handled?<Badge label="handled" color={C.green} bg={C.greenBg}/>:c.matchKind?<Badge label="known" color={C.accent} bg={C.accentGlow}/>:<Badge label="unknown" color={C.orange} bg={C.orangeBg}/>}/>);
            })}
          </QuickCard>}/>

          <QuickCard title="Follow-ups due" color={C.orange} count={dueFollowUps.length}
            empty="Nothing scheduled for today. Set follow-up dates on the lead card and they surface here.">
            {dueFollowUps.slice(0,14).map(function(f){
              return(<QuickRow key={f.kind+"-"+f.id} onClick={function(){openProspect(f.kind==="loan"?prospectFromLoan(f.raw):prospectFromLead(f.raw));}}
                title={f.name}
                sub={(f.note||f.label||"follow up")+" · due "+f.date+(f.optedOut?" · opted out · off mailings":"")}
                right={<Badge label={f.overdue?"overdue":"today"} color={f.overdue?C.red:C.orange} bg={(f.overdue?C.red:C.orange)+"18"}/>}/>);
            })}
          </QuickCard>

          <QuickCard title="Waiting on a call back" color={C.cyan} count={waitingCallIns.length}
            empty="Call-in list is clear.">
            {waitingCallIns.map(function(l){
              return(<QuickRow key={l.id} onClick={function(){openProspect(prospectFromLoan(l));}}
                title={l.b}
                sub={(l.ph||"no phone")+" · "+(l.prop||"no address")}
                right={<Badge label={l.stage} color={STC[l.stage]||C.accent} bg={(STC[l.stage]||C.accent)+"18"}/>}/>);
            })}
          </QuickCard>
        </div>}
      </div>}

      {/* ══════════════ STEP 2 — THE LEAD CARD ══════════════ */}
      {step==="card"&&p&&<div style={{maxWidth:1240,margin:"0 auto",padding:"22px 24px 140px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
          {/* Leaving to find someone else keeps every note and edit made here. */}
          <Btn small onClick={function(){flushPending();setStep("find");}} style={{color:C.textDim}}>← Someone else</Btn>
          {p.phone&&<CallBtn phone={p.phone}/>}
          {pLit&&<Badge label="Skip-trace litigator flag · off Outbound" color={C.textMuted} bg={C.surfaceAlt}/>}
          {p.kind==="loan"&&<Badge label={"Pipeline · "+((liveLoan&&liveLoan.stage)||p.stage)} color={STC[(liveLoan&&liveLoan.stage)||p.stage]||C.accent} bg={(STC[(liveLoan&&liveLoan.stage)||p.stage]||C.accent)+"18"}/>}
          {p.kind==="lead"&&<Badge label={"Marketing · "+p.statusLabel} color={C.cyan} bg={C.cyan+"18"}/>}
          {p.kind==="new"&&<Badge label="New — not saved yet" color={C.orange} bg={C.orangeBg}/>}
          {/* Information only: opted out of mailings / Outbound. A call (in or back) is fine. */}
          {p.doNotContact&&<Badge label="Opted out · off mailings & Outbound" color={C.orange} bg={C.orangeBg}/>}
          {ownerChip}
          {p.lastContactAt&&<span style={{fontSize:11,color:C.textDim}}>Last touched {fmtWhen(p.lastContactAt)}</span>}
          {p.convertedLoanId&&<span style={{fontSize:11,color:C.textDim}}>Linked file {p.convertedLoanId}</span>}
        </div>
        {/* Opened from a lead that is already a pipeline file: worked AS the
            file, so nothing is pitched or fallen out twice. */}
        {p.alreadyFile&&p.kind==="loan"&&<div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:16,padding:"11px 16px",borderRadius:12,background:C.accent+"10",border:"1px solid "+C.accent+"40"}}>
          <span style={{fontSize:13,fontWeight:800,color:C.text}}>Already a file: {p.id} — {(liveLoan&&liveLoan.stage)||p.stage}</span>
          <span style={{fontSize:11.5,color:C.textMuted}}>{p.viaLeadId?"Lead "+p.viaLeadId+" was converted. ":""}Notes, pitches and dispositions from this call go to this file.</span>
        </div>}

        <div style={{display:"grid",gridTemplateColumns:"minmax(0,1.35fr) minmax(0,1fr)",gap:18,alignItems:"start"}}>

          {/* ── LEFT: what we know ──
              Only FIVE fields are editable up front, because only five decide
              whether there is a deal: who they are, how to reach them, their
              age, what the house is worth and what they owe. Everything else
              is reference data that came off the mail list, or a number the
              calculator works out — commission, product, rate, cash to
              borrower and est. close all populate from pricing and are shown
              read-only below. Typing those by hand is how they end up wrong. */}
          <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:16,padding:"20px 22px"}}>
            {/* ── On file — what we know (10/10/26): read-only, at the top so
                it's in front of him when the lead opens. The facts block
                (LeadFacts / src/convoGuide.js) is what the CRM already holds:
                the list's likely fit (a guide — it never picks the deal
                type), the house and its loans, and our history with them —
                no fields, no talking points, no buttons but "Show all".
                The card's own reference values (rate, lender, originated…)
                and their "Correct something" fields sit in their own small
                "Card details" part BELOW it (10/10 review F7), never under
                the read-only heading. ── */}
            {(function(){
              const chips=[
                ["Current rate",p.curRate?p.curRate+"%":"",chipCover.rate],
                ["Current lender",p.lender,chipCover.lender],
                ["Originated",p.origDate,chipCover.origDate],
                ["Loan type",p.loanType,chipCover.loanType],
                ["Condo",p.condo?"Yes":"",false],
                ["LOC available",p.isReverse&&p.curAvail?$(num(p.curAvail)):"",false],
              ].filter(function(r){return r[1]&&!r[2];});
              return <>
              <div data-on-file="" style={{marginBottom:14}}>
                <div style={{display:"flex",alignItems:"center",gap:8,margin:"0 0 10px"}}>
                  <span style={{fontSize:12,fontWeight:800,color:C.accentLight,textTransform:"uppercase",letterSpacing:.7}}>{ON_FILE_TITLE}</span>
                  <div style={{flex:1,height:1,background:C.rule}}/>
                </div>
                {guideLead
                  ?<LeadFacts lead={guideLead} loans={loans} staff={staffKey} skipFileId={p.kind==="loan"?p.id:null}/>
                  :<div style={{fontSize:12,color:C.textDim}}>{p.kind==="loan"?"Nothing more on file — the lead this file came from isn't loaded.":"Nothing on file yet."}</div>}
              </div>
              <div data-card-details="" style={{marginBottom:18}}>
                <div style={{display:"flex",alignItems:"center",gap:8,margin:"0 0 8px"}}>
                  <span style={{fontSize:10,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.6}}>Card details</span>
                  <div style={{flex:1,height:1,background:C.borderLight}}/>
                  <Btn small onClick={function(){setEditDetails(function(v){return !v;});}} style={{color:editDetails?C.accentLight:C.textDim,fontSize:10.5}}>
                    {editDetails?"Done":"Correct something"}
                  </Btn>
                </div>
                {!editDetails?
                  chips.length>0&&<div data-on-file-chips="" style={{display:"flex",flexWrap:"wrap",gap:"7px 22px"}}>
                    {chips.map(function(r){
                      return <div key={r[0]} style={{flex:"0 1 auto",minWidth:0}}>
                        <div style={{fontSize:9.5,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.6}}>{r[0]}</div>
                        <div style={{fontSize:12.5,color:C.text,fontWeight:600}}>{r[1]}</div>
                      </div>;
                    })}
                  </div>
                :<div style={{display:"flex",flexWrap:"wrap",gap:12}}>
                  <Field label="Current rate"><Inp value={p.curRate||""} onChange={function(e){upd("curRate",e.target.value);}} placeholder="6.125"/></Field>
                  <Field label="Current lender"><Inp value={p.lender||""} onChange={function(e){upd("lender",e.target.value);}} placeholder="If they know it"/></Field>
                  <Field label="Originated"><Inp value={p.origDate||""} onChange={function(e){upd("origDate",e.target.value);}} placeholder="03/2019"/></Field>
                  <Field label="Condo?">
                    <label style={{display:"flex",alignItems:"center",gap:9,minHeight:42,fontSize:13,color:C.text,cursor:"pointer"}}>
                      <input type="checkbox" checked={!!p.condo} onChange={function(e){upd("condo",e.target.checked);}} style={{width:17,height:17,cursor:"pointer"}}/>
                      It's a condo / townhome
                    </label>
                  </Field>
                  {p.isReverse&&
                    <Field label="Line of credit available"><MoneyInp value={p.curAvail||""} onChange={function(e){upd("curAvail",e.target.value);}} placeholder="From their statement"/></Field>}
                </div>}
              </div>
              </>;
            })()}
            {/* HECM = refi of an existing reverse; FTR = first-time reverse;
                HS2 = FOA's HomeSafe Second behind a forward first mortgage
                that stays in place. Saved on the record (dealType, isReverse)
                and hands the calculator its scenario type, so the fields
                below are exactly what that deal needs and nothing else. */}
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:18,flexWrap:"wrap"}}>
              <div style={{display:"inline-flex",flexWrap:"wrap",padding:4,borderRadius:12,background:C.surfaceAlt,border:"1px solid "+C.border}}>
                {[{k:"hecm",l:"HECM",s:"existing reverse"},{k:"ftr",l:"FTR",s:"first-time reverse"},{k:"hs2",l:"HS2",s:"keep 1st mortgage"}].map(function(o){
                  const on=dealKindOf(p)===o.k;
                  return <button key={o.l} data-deal-type={o.k} onClick={function(){upd("isReverse",o.k==="hecm");upd("dealType",o.k);}} style={{padding:"9px 20px",borderRadius:9,border:"none",cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif",textAlign:"left",
                    background:on?C.navy:"transparent",color:on?C.white:C.textMuted,boxShadow:on?"0 1px 4px rgba(11,42,74,0.25)":"none",transition:"background .15s"}}>
                    <div style={{fontSize:14,fontWeight:800,letterSpacing:.3}}>{o.l}</div>
                    <div style={{fontSize:10,fontWeight:600,opacity:.75}}>{o.s}</div>
                  </button>;
                })}
              </div>
            </div>

            <SectionTitle first>{dealKindOf(p)==="hs2"?"The deal — HomeSafe Second":p.isReverse?"The deal — HECM":"The deal — FTR"}</SectionTitle>
            <div style={{display:"flex",flexWrap:"wrap",gap:12}}>
              <Field label="Home value"><MoneyInp value={p.homeValue||""} onChange={function(e){upd("homeValue",e.target.value);}} placeholder="450,000"/></Field>
              <Field label={dealKindOf(p)==="hs2"?"First mortgage balance (stays)":p.isReverse?"Balance":"Loan balance"}><MoneyInp value={p.mortgageBalance||""} onChange={function(e){upd("mortgageBalance",e.target.value);}} placeholder={dealKindOf(p)==="hs2"?"Their forward 1st — it stays":p.lienAmount>0?"From their statement":"0 if free and clear"}/></Field>
              {lienNote(p,hecmLimit)&&<div style={{flexBasis:"100%",fontSize:12,color:C.textMuted,marginTop:-4}}>{lienNote(p,hecmLimit)}</div>}
              {p.isReverse&&<>
                <Field label="Principal limit"><MoneyInp value={p.curPL||""} onChange={function(e){upd("curPL",e.target.value);}} placeholder="From their statement"/></Field>
                <Field label="Est. closing costs"><MoneyInp value={p.estCC||""} onChange={function(e){upd("estCC",e.target.value);}} placeholder="Title, appraisal, recording"/></Field>
              </>}
              <Field label={"Date of birth"+(p.dob&&ageFromDobStr(p.dob)?"  ·  AGE "+ageFromDobStr(p.dob):"")} wide>
                <Inp type="date" value={p.dob||""} onChange={function(e){upd("dob",e.target.value);}} style={{colorScheme:"light"}}/>
              </Field>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:10,marginTop:14,flexWrap:"wrap"}}>
              <Btn primary onClick={goCalc}>{Ic.calc} Run</Btn>
              <Btn onClick={saveAndClose}>Save Lead</Btn>
              {!p.dob&&<span style={{fontSize:11.5,color:C.orange,fontWeight:600}}>The calculator needs a date of birth.</span>}
            </div>

            <SectionTitle right={<SavedHint show={savedKey==="goal"}/>}>What they're after</SectionTitle>
            <textarea value={p.goal||""} onChange={function(e){typeNoteField("goal",e.target.value);}} onBlur={saveNoteFields}
              placeholder="Why they called, what they need the money for, who else is on title, anything that decides the deal…"
              style={{width:"100%",minHeight:70,padding:"11px 14px",borderRadius:8,border:"1px solid "+C.border,background:C.surfaceAlt,color:C.text,fontSize:13,fontFamily:"'Mulish',system-ui,sans-serif",resize:"vertical",outline:"none",boxSizing:"border-box"}}/>

            {/* ── From pricing — never typed, always derived ── */}
            <SectionTitle>From the calculator</SectionTitle>
            <PricedPanel loan={liveLoan} lastQuote={lastQuote}/>
          </div>

          {/* ── RIGHT: contact, notes, follow-up, history — shared with the numbers view ── */}
          {sidePanel}
        </div>
      </div>}

      {/* ══════════════ STEP 3 — NUMBERS ══════════════ */}
      {/* Same screen as the lead card: the calculator takes the left column,
          contact / notes / follow-up stay on the right. */}
      {step==="calc"&&p&&<div style={{maxWidth:1640,margin:"0 auto",padding:"18px 24px 150px"}}>
        <div style={{display:"grid",gridTemplateColumns:"minmax(0,2.3fr) minmax(300px,1fr)",gap:18,alignItems:"start"}}>
          <div style={{minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16,flexWrap:"wrap",background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:"12px 16px"}}>
              <Btn small onClick={function(){setStep("card");}} style={{color:C.textDim}}>← Edit the deal</Btn>
              <Badge label={dealKindOf(p)==="hs2"?"HS2":p.isReverse?"HECM":"FTR"} color={C.navy} bg={C.navy+"14"}/>
              <span style={{fontSize:11.5,color:C.textDim}}>
                {p.dob?"Age "+(ageFromDobStr(p.dob)||"?"):dealKindOf(p)==="hs2"?"No DOB":"NO DOB — the calculator needs one"} · Home {$(num(p.homeValue))} · {dealKindOf(p)==="hs2"
                  ?"1st mortgage "+$(num(p.mortgageBalance))+" (stays)"
                  :<>Owes {num(p.mortgageBalance)?$(num(p.mortgageBalance)):(p.lienAmount>0?"— need statement":$(0))}</>}
                {p.isReverse&&num(p.curPL)?" · PL "+$(num(p.curPL)):""}
              </span>
              {lastQuote&&<Badge label={"Priced "+lastQuote.time} color={C.green} bg={C.greenBg}/>}
              {pLit&&<Badge label="Skip-trace litigator flag · off Outbound" color={C.textMuted} bg={C.surfaceAlt}/>}
              <span style={{marginLeft:"auto"}}>{ownerChip}</span>
            </div>

            {pitched&&<div style={{padding:"14px 18px",background:C.greenBg,border:"1px solid "+C.green+"45",borderRadius:12,marginBottom:16,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
              <span style={{color:C.green}}>{Ic.chk}</span>
              <span style={{color:C.green,fontWeight:800,fontSize:13.5}}>Pitched “{pitched.product}” — file {pitched.loanId} is in the pipeline with everything from this call.</span>
              <Btn small primary onClick={function(){finish("Pitched to pipeline — "+pitched.loanId);}} style={{marginLeft:"auto"}}>Done</Btn>
            </div>}

            <Calc
              loans={loans} setLoans={setLoans} mktgLeads={mktgLeads}
              setNav={function(v){finish("");setNav&&setNav(v);}}
              hecmLimit={hecmLimit} rateSheets={rateSheets} setRateSheets={setRateSheets}
              initialLead={prospectToCalcLead(Object.assign({},p,{address:fullAddr(p)},p.kind==="lead"&&!(p.owner==="reece"||p.owner==="max")&&leadOwner(liveLead)?{owner:leadOwner(liveLead)}:{}))}
              staff={staffKey}
              embedded onQuoteRun={handleQuoteRun} onPitched={handlePitched}/>
          </div>
          {sidePanel}
        </div>
      </div>}
    </div>

    {/* ══════════════ STICKY ACTION BAR ══════════════
        The two corner bubbles (Whiteboard bottom-left, Ask the Library
        bottom-right — App's dockLeft / dockRight) float above this console,
        right where this bar's ends are: it keeps those corners clear, so a
        bubble never sits on "Run the Numbers →" or a Fallout button. */}
    {p&&(step==="card"||step==="calc")&&!pitched&&<div data-call-actions="" className={"cc-actions"+(dockLeft?" cc-dock-l":"")+(dockRight?" cc-dock-r":"")} style={{flexShrink:0,borderTop:"1px solid "+C.border,background:C.glass,backdropFilter:"blur(14px)",WebkitBackdropFilter:"blur(14px)",padding:"12px "+(dockRight?CALL_BAR_DOCK_PX:24)+"px 12px "+(dockLeft?CALL_BAR_DOCK_PX:24)+"px",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",boxShadow:"0 -3px 18px rgba(11,42,74,0.06)"}}>
      {(dockLeft||dockRight)&&<style>{CALL_BAR_CSS}</style>}
      {step==="card"?<>
        <Btn primary onClick={goCalc} style={{fontSize:14,padding:"13px 24px"}}>{Ic.calc} Run the Numbers →</Btn>
        <Btn onClick={saveAndClose}>Save &amp; close — still working it</Btn>
      </>:<>
        <span style={{fontSize:12,color:C.textMuted,fontWeight:600,marginRight:4}}>
          {ranNumbers?"Pitch a scenario above to send it to the pipeline — or close the call out:":"Run the numbers, then pitch or fall it out."}
        </span>
        <Btn onClick={saveAndClose}>Save &amp; close — still working it</Btn>
      </>}
      <div style={{flex:1}}/>
      {/* Three ways out. Before the numbers are run, "No App" is the honest
          one — nothing was priced, so neither Qualified nor Denied is true.
          It leads on the card step for that reason. */}
      {step==="card"&&!ranNumbers&&
        <Btn small onClick={function(){askDispo(FALLOUT_NOAPP);}}
          style={{color:C.cyan,border:"1px solid "+C.cyan+"55",background:C.cyan+"12"}}>Fallout — No App</Btn>}
      <Btn gold small onClick={function(){askDispo(FALLOUT_QUAL);}}>Fallout — Qualified</Btn>
      <Btn danger small onClick={function(){askDispo(FALLOUT_DENIED);}}>Fallout — Denied</Btn>
      {(step!=="card"||ranNumbers)&&
        <Btn small onClick={function(){askDispo(FALLOUT_NOAPP);}}
          style={{color:C.cyan,border:"1px solid "+C.cyan+"55",background:C.cyan+"12"}}>Fallout — No App</Btn>}
    </div>}

    {/* ══════════════ DISPOSITION ══════════════ */}
    {dispo&&<div style={{position:"fixed",inset:0,zIndex:420,background:"rgba(6,26,48,0.6)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:16,width:560,maxWidth:"100%",maxHeight:"88vh",overflowY:"auto",padding:26,boxShadow:"0 18px 60px rgba(6,26,48,0.35)"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
          <span style={{width:10,height:10,borderRadius:"50%",background:falloutColor(dispo.stage)}}/>
          <h3 style={{margin:0,fontSize:17,fontWeight:800,color:C.text}}>{dispo.stage}</h3>
        </div>
        <p style={{margin:"0 0 18px",fontSize:12.5,color:C.textMuted,lineHeight:1.55}}>
          {dispo.stage===FALLOUT_QUAL
            ? "The deal works — the timing doesn't. The file keeps every number you ran and comes back on the follow-up date."
            : dispo.stage===FALLOUT_DENIED
            ? "This one doesn't work. The file keeps the reason so you never re-run the same dead deal."
            : "The numbers never got run, so this isn't a dead deal — it's an unknown one. It goes on its own list: a real person who picked up the phone about a reverse mortgage, worth another shot later."}
        </p>

        {/* Switch between all three without backing out of the call */}
        <div style={{display:"flex",gap:7,marginBottom:18,flexWrap:"wrap"}}>
          {FALLOUT_STAGES.map(function(st){
            const on=dispo.stage===st;const col=falloutColor(st);
            return <button key={st} onClick={function(){switchDispo(st);}} style={{flex:"1 1 30%",minWidth:120,padding:"10px 10px",borderRadius:9,cursor:"pointer",fontSize:11.5,fontWeight:800,fontFamily:"'Mulish',system-ui,sans-serif",
              border:"1px solid "+(on?col:C.border),background:on?col+"16":"transparent",color:on?col:C.textMuted}}>{st.replace("Fallout — ","")}</button>;
          })}
        </div>

        <label style={{display:"block",fontSize:10,fontWeight:800,color:C.textDim,marginBottom:6,textTransform:"uppercase",letterSpacing:.6}}>Why</label>
        <select value={dispoReason} onChange={function(e){pickReason(e.target.value);}}
          style={{width:"100%",padding:"12px 14px",borderRadius:8,border:"1px solid "+C.border,background:C.surfaceAlt,color:C.text,fontSize:14,fontFamily:"'Mulish',system-ui,sans-serif",marginBottom:12}}>
          {(FALLOUT_REASONS[dispo.stage]||[]).map(function(r){return <option key={r} value={r}>{r}</option>;})}
        </select>

        <label style={{display:"flex",alignItems:"center",gap:9,marginBottom:14,fontSize:12.5,color:dispoDnc?C.red:C.textMuted,cursor:"pointer",fontWeight:dispoDnc?700:500}}>
          <input type="checkbox" checked={dispoDnc} onChange={function(e){setDispoDnc(e.target.checked);}} style={{width:16,height:16,cursor:"pointer"}}/>
          Mark do not contact — drop them from every future mailing and list
        </label>

        <label style={{display:"block",fontSize:10,fontWeight:800,color:C.textDim,marginBottom:6,textTransform:"uppercase",letterSpacing:.6}}>Anything else worth remembering</label>
        <textarea value={dispoNote} onChange={function(e){setDispoNote(e.target.value);}} placeholder="Optional — goes on the file with today's date. Kept as a note even if you back out."
          style={{width:"100%",minHeight:70,padding:"11px 14px",borderRadius:8,border:"1px solid "+C.border,background:C.surfaceAlt,color:C.text,fontSize:13,fontFamily:"'Mulish',system-ui,sans-serif",resize:"vertical",outline:"none",boxSizing:"border-box",marginBottom:14}}/>

        <label style={{display:"block",fontSize:10,fontWeight:800,color:C.textDim,marginBottom:6,textTransform:"uppercase",letterSpacing:.6}}>Call back on</label>
        <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:9}}>
          {[{d:30,l:"30 days"},{d:90,l:"90 days"},{d:180,l:"6 months"},{d:365,l:"1 year"}].map(function(o){
            const on=dispoFollowUp===isoDatePlus(o.d);
            return <button key={o.d} onClick={function(){setDispoFollowUp(isoDatePlus(o.d));}} style={{padding:"5px 11px",borderRadius:20,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif",
              border:"1px solid "+(on?C.orange:C.border),background:on?C.orangeBg:"transparent",color:on?C.orange:C.textMuted}}>{o.l}</button>;
          })}
          <button onClick={function(){setDispoFollowUp("");}} style={{padding:"5px 11px",borderRadius:20,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif",border:"1px solid "+C.border,background:"transparent",color:C.textDim}}>None</button>
        </div>
        <Inp type="date" value={dispoFollowUp} onChange={function(e){setDispoFollowUp(e.target.value);}} style={{colorScheme:"light"}}/>

        {lastQuote&&<div style={{marginTop:16,padding:"11px 14px",borderRadius:9,background:C.surfaceAlt,border:"1px solid "+C.border}}>
          <div style={{fontSize:10,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.6,marginBottom:5}}>Saved with this file</div>
          <div style={{fontSize:12,color:C.text}}>{sessionQuotes.current.length>1?sessionQuotes.current.length+" pricing runs this call · last ":"Pricing run "}{lastQuote.date} {lastQuote.time} — {lastQuote.productCount} product{lastQuote.productCount===1?"":"s"}{lastQuote.top[0]?", best: "+lastQuote.top[0].name+" ("+$(lastQuote.top[0].avail)+" available)":""}</div>
        </div>}

        <div style={{display:"flex",gap:10,marginTop:22,flexWrap:"wrap",alignItems:"center"}}>
          <Btn primary onClick={confirmDispo}>Confirm — {dispo.stage}</Btn>
          {/* Backing out keeps a note typed here as an ordinary note. */}
          <Btn onClick={leaveDispo}>Back to the call</Btn>
          {/* Never trap him in here. If he opened this record to fix a phone
              number and nothing more, closing out is a legitimate answer. */}
          <Btn small onClick={function(){setDispo(null);saveAndClose();}} style={{color:C.textDim,marginLeft:"auto",fontSize:11}}>
            Skip — just save and close
          </Btn>
        </div>
      </div>
    </div>}
  </div>);
}

// ── What the calculator produced ──────────────────────────────
// Read-only on purpose. Commission, product, rate, cash to borrower and the
// estimated close date are all outputs of pricing — if they can be typed here
// they will eventually disagree with the numbers that were actually quoted,
// and the wrong one is the one that ends up on a file.
// The two exceptions are figures Reece typed himself, and they are labelled
// as such so they never read as calculator outputs: a deal entered by hand
// from the lender's paperwork (deal.manual — Pipeline's "Add a deal") and a
// pitch priced on the lender's own amount (deal.lenderAmount — the "Lender's
// amount" box on a calculator card).
function PricedPanel({loan,lastQuote}){
  const deal=(loan&&loan.deal)||{};
  loan=loan||{};
  const best=lastQuote&&(lastQuote.top||[])[0];
  const hasPitch=!!deal.product;
  const manual=hasPitch&&!!deal.manual;
  const lenderFig=hasPitch&&!manual&&Number(deal.lenderAmount)>0;
  // A HomeSafe Second pitch: a loan amount (FOA's, or the calculator's
  // estimate — labelled either way) behind a first lien that stays.
  const hs2=hasPitch&&!manual&&deal.dealType==="hs2";
  // Priced but not pitched: the run's best option, labelled the same way —
  // a HomeSafe Second run (or an HS2 tier on top) is badged, its amount is
  // FOA's figure or the calculator's estimate (said so), the first lien
  // stays and the CLTV shows; any card priced on a lender's amount says so.
  // Read off the run's own snapshot (Calc's inputs: dealType, firstLien,
  // lenderAmounts; quoteSnapshot's lenderAmount / calcAmount / hs2Estimate).
  const qIn=(lastQuote&&lastQuote.inputs)||{};
  const runHs2=!hasPitch&&!!best&&(qIn.dealType==="hs2"||/^HomeSafe (2nd|Second)\b/i.test(String(best.name||"")));
  const runLenderAmt=!hasPitch&&best?(best.lenderAmount!=null?Number(best.lenderAmount):Number((qIn.lenderAmounts||{})[String(best.lender||"")+"|"+String(best.name||"")])||0):0;
  const runLenderFig=runLenderAmt>0;
  const runFirst=qIn.firstLien!=null?qIn.firstLien:qIn.mortgageBalance;
  const runCltv=runHs2?cltvOf(runFirst,best.gross,qIn.homeValue):null;
  if(!hasPitch&&!best){
    return(<div style={{padding:"14px 16px",border:"1px dashed "+C.border,borderRadius:9,fontSize:12,color:C.textDim,lineHeight:1.55}}>
      Nothing priced yet. Run the numbers and the product, rate, cash to the borrower
      and your commission fill in here on their own.
    </div>);
  }
  const rows=hasPitch?[
    ["Product",deal.product],
    ["Lender",deal.lender],
    ["Rate / terms",deal.rate],
    manual?["Loan amount (entered by hand)",$(deal.grossPL)]
      :lenderFig?[hs2?"HomeSafe Second amount (lender figure)":"Principal limit (lender figure)",$(deal.grossPL),Number(deal.calcAmount)>0?"calculator said "+$(deal.calcAmount):""]
      :hs2?["HomeSafe Second amount (estimate)",$(deal.grossPL),"confirm in FOA's software"]
      :["Principal limit",$(deal.grossPL)],
    [hs2?"First lien (stays in place)":"First lien",deal.firstLien!=null?$(deal.firstLien):""],
    ["CLTV",deal.cltv!=null&&!isNaN(Number(deal.cltv))?cltvText(deal.cltv):""],
    // HomeSafe Second is PL in, pay out (Reece, 10/10/26): its costs and
    // cash are FOA's software's business — the panel shows the amount and
    // the pay, like the calculator's HS2 cards (the deal still records them).
    ["Total costs",hs2?"":$(deal.totalCosts)],
    ["Cash to borrower",hs2?"":$(deal.cashToBorrower)],
    // HECM: HUD's initial disbursement limit caps what can be drawn in year 1.
    ["Available year 1",deal.year1Cash!=null&&Number(deal.year1Cash)<Number(deal.cashToBorrower||0)?$(deal.year1Cash):""],
    ["Your commission",$(loan.comm)],
    ["Est. close",loan.estClose],
  ]:[
    ["Best option",best.name],
    ["Lender",best.lender],
    runLenderFig?[runHs2?"HomeSafe Second amount (lender figure)":"Principal limit (lender figure)",$(runLenderAmt),Number(best.calcAmount)>0?"calculator said "+$(best.calcAmount):""]
      :runHs2?["HomeSafe Second amount (estimate)",$(best.gross),"confirm in FOA's software"]
      :null,
    runHs2?["First lien (stays in place)",num(runFirst)>0?$(runFirst):""]:null,
    runHs2?["CLTV",runCltv!=null?cltvText(runCltv):""]:null,
    runHs2?["Your pay",$(best.comp)]:["Cash to borrower",$(best.avail)],
    ["Available year 1",best.year1!=null&&Number(best.year1)<Number(best.avail||0)?$(best.year1):""],
    // A HomeSafe Second is a loan amount, not a principal limit.
    runHs2?null:["Net principal limit",$(best.net)],
    ["Priced",lastQuote.date+" "+lastQuote.time],
  ].filter(Boolean);
  return(<div>
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:9,flexWrap:"wrap"}}>
      {manual
        ?<Badge label={MANUAL_DEAL_LABEL} color={C.purple} bg={C.purple+"18"}/>
        :<Badge label={hasPitch?"pitched":"priced — not pitched"} color={hasPitch?C.green:C.cyan} bg={(hasPitch?C.green:C.cyan)+"18"}/>}
      {(hs2||runHs2)&&<Badge label={HS2_LOAN_TYPE} color={C.navy} bg={C.navy+"14"}/>}
      {(lenderFig||runLenderFig)&&<Badge label="Lender's amount" color={C.purple} bg={C.purple+"18"}/>}
      <span style={{fontSize:10.5,color:C.textDim}}>{manual?"the lender's figures as typed, not the calculator's"
        :lenderFig||runLenderFig?"filled in by the calculator, on the lender's amount you typed"
        :runHs2?"filled in by the calculator — "+HS2_ESTIMATE_TAG
        :"filled in by the calculator"}</span>
    </div>
    {lenderFig&&deal.lenderAmountRecheck&&<div style={{fontSize:11,color:C.orange,fontWeight:700,marginBottom:7}}>
      Re-check — the lender's amount was entered before the DOB / value / balance changed
    </div>}
    <div style={{background:C.surfaceAlt,border:"1px solid "+C.border,borderRadius:9,padding:"10px 14px"}}>
      {rows.filter(function(r){return r[1]&&r[1]!=="—";}).map(function(r,i,arr){
        return(<div key={r[0]} style={{display:"flex",justifyContent:"space-between",gap:12,padding:"5px 0",borderBottom:i<arr.length-1?"1px solid "+C.borderLight:"none"}}>
          <span style={{fontSize:11.5,color:C.textDim}}>{r[0]}</span>
          <span style={{fontSize:12.5,color:r[0]==="Your commission"?C.gold:C.text,fontWeight:r[0]==="Your commission"?800:600,textAlign:"right"}}>
            {r[1]}
            {r[2]&&<span style={{display:"block",fontSize:10.5,fontWeight:500,color:C.textDim}}>{r[2]}</span>}
          </span>
        </div>);
      })}
    </div>
  </div>);
}

// ── Quick-list card + row ─────────────────────────────────────
function QuickCard({title,color,count,empty,children}){
  const has=React.Children.count(children)>0;
  return(<div style={{background:C.card,border:"1px solid "+color+"38",borderRadius:14,padding:"16px 18px"}}>
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
      <h3 style={{margin:0,fontSize:13,fontWeight:800,color:color}}>{title}</h3>
      <span style={{fontSize:11,fontWeight:800,color:color,background:color+"18",padding:"1px 9px",borderRadius:10}}>{count}</span>
    </div>
    {has?<div style={{display:"flex",flexDirection:"column",gap:5,maxHeight:330,overflowY:"auto"}}>{children}</div>
        :<div style={{padding:"16px 10px",fontSize:11.5,color:C.textDim,lineHeight:1.5,border:"1px dashed "+C.border,borderRadius:9}}>{empty}</div>}
  </div>);
}
function QuickRow({onClick,title,sub,right}){
  return(<div onClick={onClick} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 11px",borderRadius:9,background:C.surfaceAlt,border:"1px solid "+C.border,cursor:"pointer"}}
    onMouseEnter={function(e){e.currentTarget.style.background=C.cardHover;}}
    onMouseLeave={function(e){e.currentTarget.style.background=C.surfaceAlt;}}>
    <div style={{flex:1,minWidth:0}}>
      <div style={{fontSize:12.5,fontWeight:700,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{title}</div>
      <div style={{fontSize:10.5,color:C.textDim,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{sub}</div>
    </div>
    {right}
  </div>);
}

// ── Merged contact + pricing timeline, newest first ───────────
// Two sources, deduped by timestamp: the uncapped `scenarios` / `contact_log`
// tables when they exist, and the capped jsonb copy on the record otherwise.
// Both are merged rather than one replacing the other, so history written
// before the migration still shows alongside everything after it.
function Timeline({contactLog,quoteLog,notes,dbScenarios,dbContacts,dbLoaded}){
  const items=useMemo(function(){
    const out=[];
    const seen=Object.create(null);
    // Round to the second: the same event lands in both stores microseconds
    // apart, and two identical rows in the timeline reads as a double-call.
    const push=function(it){
      const k=Math.floor((it.epoch||0)/1000)+"|"+(it.kind||"")+"|"+String(it.text||"").slice(0,60);
      if(seen[k])return;
      seen[k]=1;out.push(it);
    };
    // Database first — it wins on conflict because it is the complete copy.
    (dbContacts||[]).forEach(function(c){
      // A Calls to return Done's `at` is the newest miss it covers; when he
      // tapped it is created_at.
      const d=new Date(c.kind==="callback-done"&&c.created_at?c.created_at:c.at);
      push({epoch:d.getTime(),kind:c.kind||"note",text:c.body||"",
        when:d.toLocaleDateString()+" "+d.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})});
    });
    (dbScenarios||[]).forEach(function(s){
      const d=new Date(s.run_at);
      push({epoch:d.getTime(),kind:s.kind==="pitch"?"pitch":"quote",
        when:d.toLocaleDateString()+" "+d.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),
        text:(s.kind==="pitch"?"Pitched ":"Ran pricing — ")+
          (s.kind==="pitch"?(s.product||"")+" ("+(s.lender||"")+")":(s.product_count||0)+" product"+(s.product_count===1?"":"s")+(s.product?" · best "+s.product:""))+
          (s.cash_to_borrower!=null?" · "+$(s.cash_to_borrower)+" available":""),
        rate:s.swap_rate!=null?Number(s.swap_rate):null,
        detail:{top:s.all_products||[]}});
    });
    (contactLog||[]).forEach(function(e){push({epoch:e.epoch||0,kind:e.type||"note",text:e.text,when:(e.date||"")+" "+(e.time||"")});});
    (quoteLog||[]).forEach(function(q){
      const best=(q.top||[])[0];
      push({epoch:q.epoch||0,kind:"quote",when:(q.date||"")+" "+(q.time||""),
        text:"Ran pricing — "+(q.productCount||0)+" product"+(q.productCount===1?"":"s")+(best?" · best "+best.name+" ("+$(best.avail)+" available)":""),
        detail:q});
    });
    out.sort(function(a,b){return (b.epoch||0)-(a.epoch||0);});
    return out;
  },[contactLog,quoteLog,dbScenarios,dbContacts]);

  // "callback-done" = a Done on the Calls to return list (src/callbacks.js).
  const tone={note:C.accent,call:C.cyan,quote:C.green,pitch:C.green,disposition:C.gold,created:C.textDim,mail:C.textDim,"callback-done":C.cyan};
  const kindWord={"callback-done":"call back"};

  if(items.length===0&&!notes){
    return <div style={{padding:"16px 10px",fontSize:11.5,color:C.textDim,border:"1px dashed "+C.border,borderRadius:9,lineHeight:1.5}}>
      Nothing logged yet. Every note, pricing run and disposition from here on lands in this list with its timestamp.
    </div>;
  }
  return(<div style={{maxHeight:420,overflowY:"auto",display:"flex",flexDirection:"column",gap:8}}>
    {items.map(function(it,i){
      const col=tone[it.kind]||C.textDim;
      return(<div key={i} style={{display:"flex",gap:10,paddingBottom:8,borderBottom:i<items.length-1?"1px solid "+C.borderLight:"none"}}>
        <span style={{width:7,height:7,borderRadius:"50%",background:col,marginTop:6,flexShrink:0}}/>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:12,color:C.text,lineHeight:1.5,whiteSpace:"pre-wrap"}}>{it.text}</div>
          <div style={{fontSize:10,color:C.textDim,marginTop:2}}>{it.when}{it.kind!=="note"?" · "+(kindWord[it.kind]||it.kind):""}</div>
          {it.detail&&(it.detail.top||[]).length>0&&<div style={{marginTop:5,paddingLeft:2}}>
            {it.detail.top.map(function(t,k){return <div key={k} style={{fontSize:10.5,color:C.textMuted}}>{t.name} — {t.lender} · net {$(t.net)} · available {$(t.avail)}</div>;})}
          </div>}
        </div>
      </div>);
    })}
    {notes&&<div style={{marginTop:6,paddingTop:10,borderTop:"1px solid "+C.border}}>
      <div style={{fontSize:10,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.6,marginBottom:4}}>Imported / list notes</div>
      <div style={{fontSize:11.5,color:C.textMuted,whiteSpace:"pre-wrap",lineHeight:1.5,maxHeight:150,overflowY:"auto"}}>{notes}</div>
    </div>}
  </div>);
}


export default CallConsole;
